import {
  Scene,
  Vector2,
  Vector3,
  Mesh,
  ArcRotateCamera,
  CSG,
  MeshBuilder,
  AbstractMesh,
  ActionManager,
  Color4,
  Color3,
  Vector4,
  VertexBuffer,
  ExecuteCodeAction,
  ShadowGenerator,
  DirectionalLight,
  Matrix,
  TmpVectors,
  VertexData,
  BoundingBox,
  AnimationGroup,
  PointerDragBehavior,
  Plane,
  Tools,
  Axis,
  Ray,
  InstancedMesh,
  PointerEventTypes,
  PointerInfo,
  Observer,
  InitializeCSG2Async,
  Quaternion,
} from '@babylonjs/core'
import earcut from 'earcut'
import {
  BuildingElementType,
  BuildingViewMode,
  Storey,
  Roof,
  Wall,
  BuildingElementSubType,
  Building,
  BuildingElementStatus,
  BuildingWindowSizes,
  BuildingDoorSizes,
  BuildingBeamSizes,
  Foundation,
  HideShowOption,
} from '../models/Construction'
import {
  ShearWall,
  LoadBearingWall,
  RectangleFramingElement,
  RectangleFramingElementType,
  PileCap,
  Pile,
  StructuralBackgroundColor,
} from '../models/Structural'
import { SceneManager } from './sceneManager'
import { MaterialManager } from './materials/materialManager'
import {
  IBuildingViewMode,
  SingleStoreyMode,
  StackableViewMode,
  WholeBuildingMode,
} from './buildingViewMode'
import { ShadowOnlyMaterial } from '@babylonjs/materials'
import {
  calculateContour,
  deg2Rad,
  rad2Deg,
  rayBoundingBoxIntersection,
  isAntiParallel,
  lineToLine2DIntersection,
  isParallel,
} from '../utils/MathUtil'
import {
  computeBoundingBox,
  createWallGaps,
  cutOpeningHolesInWallMesh,
  getBuildingBoundingBox,
  importMeshWithMaterials,
  isBeamMesh,
  isBimObjectMesh,
  isDoorMesh,
  isExteriorWallMesh,
  isFoundationMesh,
  isGroundMesh,
  isHeaderMesh,
  isRidgeMesh,
  isRoofMesh,
  isSelectableMesh,
  isSelectionIgnored,
  isSketchMesh,
  isSpanMesh,
  isStoreyMesh,
  isStructuralMesh,
  isUnknownWallMesh,
  isUtilityMesh,
  isWallGapMesh,
  isWallMesh,
  isWallOrWallGap,
  isWindowMesh,
  serializeMeshWithMaterials,
} from './utils'
import store from '../store'
import { BuildingStructuralTypes } from '../models/Structural'
import { delay, getEnums } from '../utils/Common'
import { ConstructionPage, ConstructionTab } from '../reducers/construction'
import { SelectionManager } from './selection/selectionManager'
import { EventType, StoreyElementsVisibility } from '../utils/EventEmitter'
import {
  CodeValidationDiscipline,
  CodeValidationRule,
  CodeValidationRuleStatus,
  CodeValidationStatus,
} from '../models/Validation'
import {
  CreateDoorPayload,
  CreateWindowPayload,
  updateBuildingElementStatus,
} from '../actions/construction'
import {
  deselectCodeValidationRule,
  selectCodeValidationRule,
  setCurrentValildationMessages,
} from '../actions/validation'
import { v4 as uuid } from 'uuid'
import { StructuralAnimationStatus } from '../reducers/structural'
import { SeparateElementUI } from './tools/useSeparateWallTool'
import { WallAnchor } from './tools/useEditWallTool'
import { BimManager } from './bimManager'
import { BimObject } from '../models/Bim'
import { buildBimObjectRefIdMap, getBimObjectRefId } from '../utils/BimUtil'

type CreateWallOptions = Pick<
  Wall,
  | 'id'
  | 'length'
  | 'height'
  | 'thickness'
  | 'center_point'
  | 'bottom_elevation'
  | 'angle'
  | 'type'
>

export type SnapWallElementsPayload = {
  firstMesh: AbstractMesh
  secondMesh: AbstractMesh
}

export type CreateBuildingPayload = {
  building: Building
  removedWallIds?: string[]
  addedWallIds?: string[]
}

const dragCursor =
  'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAAhCAYAAAC803lsAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAOvSURBVHgBxVdNTBNBFH4lXJU10cQYI6uE4AlL8KaJRQ16MIB/eDCBCjEe+dF4IoJ60sQUMJF4IIUISrhQLiYaSatHoWkxMQEU2qpwAGm3lHhd37fM1ArFLmyLXzK7M7O7M9978/6WyCJ0XXdwG+HWTv8LvHm9zzuu71VO6N2dL3ioN9NOAxro7xvV86k02TwjYyBTQzsF3sz1oKPH2LzLNWDcG+rbDM34Jz7H+Lmdcg1JApviWOZmv8dAJBaNx+639+hF6nn960wkxO+plEvAMC/XNEnJHSADIgwvtz5oBs8ZHZRLQFJsItUvjRVEJFFoZ0eOJ4WU4vGMGcfBcJMF5JE1KPFYghRlF/pxsgCrRFQtviqJaGQBaYmIaKmSCWjaCmUDeWlIuJ52DXpDcz8CsIEM3yualqBC9QD6YcoGEWwKy7/d8riZ44TZ75U4EykweTSLi1HvZjEmT5BQoz8179mKRseox0sf/UPa4SMHK2w2W6bFDY2YsRG486uXrx3nKm+ps1++edeTyccEHlyrvWM8GPP2QtUe7taIWKClbBLGhQmGJRFoxAz4myCv1z8ZmKoHmXH/EMiUSWFtPOgsL6tt+hScNiTDeQsJjT7ULsdqoRzvNsaHCvdrVy61Kk3N16muvtpHf+wkkko85U7RaNxdeeamiv5EYDjIRMokEcd73wSOhapqTlN1tYM9YZW0+ApB7XHRNCF5OLzw1zgVwmhJFXcIUCCEk4KsqYeo0XmP6pxV1Ot+2Mdkbtgwjzqiu3PQxYZK7/hoTjmOt/B0EGulaQUpffu+PSeVJ667yc0i4fk1wpEFMV7YIIicB/yBYTpWdtSWlAb5A9kTeQMZ1UwcgZch4SHfpMwhF9lFLEJzitYhWqy7cyBZQqRNjrCXluZHern9qqncscTuiAXhkmQCWFOWEP3u0X9XdCAjUnzGSmuGCYDI9FQokOldBMpWFhIJUqzvpGwBC8paJNO7xtF3PJNF04YSIZ8sIJH4lSkFJMGeAfuAA/jSBcptZ1+Zh7aSZ5iAZ7NobaUMUKSrZgOWiKAWEcErQhaxJSJwv+WlWEAYm7K+FoGnbbdWNU0EgYpTgbOk+IKdaxV4iYp5YSNwRzfXMSOTwWkQLaRcQv4uIBaEQ/N6g7PNaJzIYrhj3mxUXg/bFt83wjrqFlmHlNpL6INvwsglb94+DxcVH7qIlE+5Btx2eVkLQXr5z4uUEOU5Pdd/d2nIqIiQIIOjEgZsOrhlnczSGoG+bJD4DUtvwhLLCCgiAAAAAElFTkSuQmCC), auto'
const createCursor =
  'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAYCAYAAAARfGZ1AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAM4SURBVHgBrVU9TFNRFD7v9r22UaD1h8Jg5ZHo1KbUqEsHwMDCIhBM3CzGyAaDOooojuqgTLJQdCJRIy46aKgmMDmgqZNDHy2ExgbbIiLta9/xnltqgNJnUb7k5d2/893vnnPuuRL8BU6IOhWL0sMYOBHRKQYR51mhMJ+AZs3MVqo0Ua8s9jNgQc7UDpXNwwYYk0n9WKgq8kZYVlHBCSJ1N1ng4qUDEGi1grfFCnXO4vLIJx2+8G/q6TrMvc/RkCbpuXOmJ3FZYz0ueSl1sn4ZHz9aw0zKwHujq9jbkUQaa1CWxL/j9DccvpbBmJbH19O/8MyJBM2lXJZYz67E9bDoJ2NaSEZEXiI0+27yTdKpAgb7VkSfu9O/zS1FVxgz3A3qi7dH4f7dHzD1ZB2qhdenANn1X1iBuQ85LavnTqWhOc1o0lCMEf4TxES6F2JC5LMuiCeeHeZxYapNsY0I5ZuqoxS4q4MHofNsEv4Vow8csJo2xMm5+kOsoBTaaeLGcC3cup4xNX730QWUQZVApCSSqwebovQzhizoaVHEJPcXmKHOIZnOk+q4loeu83beY90MJfS7VVnk7X7gzassFMWiKnMtTq9PFhdjJ8gFgTbrn77DwaCr2w4ZrpAQ1wplp81kDPA4ZWqqspkKQd5q3zYWaLVxchTtiCNXRh5fyIP7+AHRJnItvlBQtyosgQy3Gge+NsAwDzpfD5Xg9VlFanLwPEdJI5fQRdgPeFpK8eOVEyRjmjruJhk8Phn+F4E2Gy9mWbr6kyyr6yEaHB9bg4GhGlPDDn7BzFwyMFgjiMUaix5mVAMAjYfjYz/FrlReK2F1M0t2AwWfLiJdJB7uUGKjWRO1JZvP3+aG2uW+76I+7NU9REx1iU7PVWvMkrtD44Kc1BuAvRRlKgGh50fENa4GlGWlgkeqiYdUly2kYu/iRb9U0yPzORy6kiqr69Tv7UzibHhDrKPHhB4ZemxMlTTal1VOECUSIp4NZ5EQ0/RNMl30aZxeI7GxvDjTaI+qO7kqVqJG/kAjQhAkqb3kV/ooEzIZLAYXMSxJMJmo9oHe7SSGrvv5JuL5kiQpLRmQ3qjVX6bTPNNM8Bvpe9WNhwYbFwAAAABJRU5ErkJggg==), auto'

export class ConstructionBuilder {
  private sceneManager: SceneManager
  private scene?: Scene
  private camera?: ArcRotateCamera
  private materialManager: MaterialManager
  private selectionManager: SelectionManager
  private buildingViewMode: IBuildingViewMode

  private rectangleFramingElementMeshes: Map<BuildingStructuralTypes, Mesh>
  private pileElementMesh: Mesh

  private topFaceColor: Color3 = Color3.Black()
  private otherFacesColor: Color3 = Color3.White()
  private edgesColor = Color3.FromHexString('#6E6E6E')
  private faceColors = [
    Color4.FromColor3(this.otherFacesColor, 1),
    Color4.FromColor3(this.otherFacesColor, 1),
    Color4.FromColor3(this.otherFacesColor, 1),
    Color4.FromColor3(this.otherFacesColor, 1),
    Color4.FromColor3(this.topFaceColor, 1),
    Color4.FromColor3(this.otherFacesColor, 1),
  ]

  private currentBuildingProcessId: number = 0
  private currentAnimationProcessId: number = 0

  groundContour: Vector2[] = []

  transparencyFunction = () => {
    if (!this.camera) {
      return
    }

    const viewRadius = Math.PI / 2
    const cameraPosition = this.camera.position
    const cameraTarget = this.camera.getTarget()
    const cameraNormal = cameraTarget.subtract(cameraPosition).normalize()

    const selectedConstructionTab =
      store.getState().construction.selectedConstructionTab
    const selectedConstructionPage =
      store.getState().construction.selectedConstructionPage
    const allWallsShouldBeTransparent =
      selectedConstructionTab === ConstructionTab.Editor &&
      selectedConstructionPage === ConstructionPage.Engineering

    const filteredWallMeshes =
      this.scene?.meshes.filter(
        (mesh) =>
          mesh.isVisible &&
          ((allWallsShouldBeTransparent && isWallMesh(mesh)) ||
            isExteriorWallMesh(mesh) ||
            isUnknownWallMesh(mesh)) &&
          !mesh.metadata.codeValidationStatus,
      ) ?? []

    for (const wallMesh of filteredWallMeshes) {
      const vector1 = new Vector2(
        wallMesh.metadata.wallNormal.x,
        wallMesh.metadata.wallNormal.z,
      )
      const vector2 = new Vector2(cameraNormal.x, cameraNormal.z)
      const angle = Math.acos(Vector2.Dot(vector1, vector2))
      wallMesh.refreshBoundingInfo({})

      if (
        !wallMesh.material?.metadata ||
        (wallMesh.material!.metadata &&
          !wallMesh.material!.metadata.overrideTransparency)
      ) {
        if (angle < viewRadius || allWallsShouldBeTransparent) {
          wallMesh.metadata.isTransparent = true
          wallMesh.material = this.materialManager.getInactiveMaterial(
            BuildingElementType.Wall,
          )
        } else {
          wallMesh.metadata.isTransparent = false
          wallMesh.material = this.materialManager.getNormalMaterial(
            BuildingElementType.Wall,
          )
        }
      }
    }
  }

  constructor() {
    this.sceneManager = SceneManager.getInstance()
    this.scene = this.sceneManager.getScene()
    this.camera = this.sceneManager.getCamera()

    this.materialManager = MaterialManager.getInstance()
    this.materialManager.createConstructionMaterials()
    this.materialManager.createValidationMaterials()
    this.materialManager.createStructuralMaterials()
    this.materialManager.createGridMaterial()
    this.buildingViewMode = new WholeBuildingMode()

    this.selectionManager = SelectionManager.getInstance()

    this.rectangleFramingElementMeshes = getEnums(
      BuildingStructuralTypes,
    ).reduce<Map<BuildingStructuralTypes, Mesh>>((acc, [value, type]) => {
      // Ignore Shear and LoadBearing Walls
      if (
        type === BuildingStructuralTypes.ShearWall ||
        type === BuildingStructuralTypes.LoadBearingWall
      ) {
        return acc
      }

      const rectangleFramingElementMesh = MeshBuilder.CreateBox(
        `${value}RectangleFramingElementMesh`,
        {},
        this.scene,
      )
      rectangleFramingElementMesh.material =
        this.materialManager.getStructuralMaterial(type)
      rectangleFramingElementMesh.isVisible = false
      rectangleFramingElementMesh.isPickable = false
      rectangleFramingElementMesh.registerInstancedBuffer(
        VertexBuffer.ColorKind,
        3,
      )
      rectangleFramingElementMesh.instancedBuffers.color = Color3.FromHexString(
        StructuralBackgroundColor[type],
      )
      rectangleFramingElementMesh.metadata = {
        type: BuildingElementType.Utility,
      }

      acc.set(type, rectangleFramingElementMesh)

      return acc
    }, new Map())

    this.pileElementMesh = MeshBuilder.CreateCylinder(
      'PileElementMesh',
      {
        height: 1,
        diameter: 1,
      },
      this.scene,
    )
    this.pileElementMesh.material = this.materialManager.getStructuralMaterial(
      BuildingStructuralTypes.Foundation,
    )
    this.pileElementMesh.isVisible = false
    this.pileElementMesh.isPickable = false
    this.pileElementMesh.registerInstancedBuffer(VertexBuffer.ColorKind, 3)
    this.pileElementMesh.instancedBuffers.color = Color3.FromHexString(
      StructuralBackgroundColor[BuildingStructuralTypes.Foundation],
    )
    this.pileElementMesh.metadata = {
      type: BuildingElementType.Utility,
    }
  }

  getStoreys() {
    const meshes = SceneManager.getInstance().getScene()?.meshes ?? []

    return meshes.filter((mesh) => isStoreyMesh(mesh))
  }

  getRoofs() {
    const meshes = SceneManager.getInstance().getScene()?.meshes ?? []

    return meshes.filter((mesh) => isRoofMesh(mesh))
  }

  async createBuilding(
    building: Building,
    options?: { showAnimation?: boolean; itemIds?: string[] },
    sceneInstanceId?: number,
  ) {
    await InitializeCSG2Async()

    // Clear scene
    if (options && options.itemIds) {
      for (const itemId of options.itemIds) {
        const elementToRemove = this.scene?.getMeshById(itemId)
        if (elementToRemove) {
          this.removeBuildingElement(elementToRemove)
        }
      }
    } else {
      this.scene?.meshes.forEach((mesh) => {
        if (!isUtilityMesh(mesh)) {
          this.scene?.removeMesh(mesh)
          mesh.dispose()
        }
      })
      if (this.scene) {
        this.scene.meshes = this.scene.meshes.filter((m) => isUtilityMesh(m))
      }
    }

    // Breaks current animation
    const processId = ++this.currentBuildingProcessId
    const isCurrentProcessValid = () =>
      processId === this.currentBuildingProcessId &&
      this.sceneManager.isActualSceneInstance(sceneInstanceId)

    let animationDurationPerMesh = 0
    if (options && options.showAnimation) {
      const buildingHasAllElements = Object.values(
        store.getState().construction.convertData.building.has_elements,
      ).every((value) => !!value)
      if (buildingHasAllElements) {
        const totalAnimationDuration = 10000 // 10 seconds
        let elementsNb = 0
        building.storeys.forEach((storey) => {
          if (storey.walls) {
            elementsNb += storey.walls.length
            elementsNb += storey.walls.flatMap((wall) => wall.doors).length
            elementsNb += storey.walls.flatMap((wall) => wall.windows).length
            elementsNb += storey.rooms.length
          }
        })
        elementsNb += building.roofs.length
        animationDurationPerMesh = totalAnimationDuration / elementsNb
      }
    }

    const renderElement = async (
      condition: boolean,
      elementType: BuildingElementType,
      elementSubtype: BuildingElementSubType | undefined,
      level: number,
      createFunction: () => Mesh[],
      statusComplete?: () => void,
    ) => {
      if (!condition || !isCurrentProcessValid()) {
        return
      }

      if (statusComplete) {
        this.updateElementStatus({
          elementType,
          elementSubtype,
          status: BuildingElementStatus.Running,
        })
      }
      const meshes = createFunction()
      if (!options?.itemIds) {
        this.updateCameraPosition()
      } else {
        meshes.map((mesh) => mesh.computeWorldMatrix())
      }

      if (options && options.showAnimation) {
        meshes.forEach((mesh) => (mesh.isVisible = false))
        if (level !== 0) {
          for (const mesh of meshes) {
            const parent = mesh.parent as AbstractMesh
            parent.isVisible = false
            parent
              .getChildMeshes()
              .filter((mesh) => isSketchMesh(mesh))
              .forEach((sketchLineMesh: AbstractMesh) => {
                sketchLineMesh.isVisible = true
              })
          }
        }

        await this.showSketchAnimation(meshes, level, animationDurationPerMesh)
      } else {
        for (const mesh of meshes) {
          const sketchMesh = mesh.getChildMeshes().find((m) => isSketchMesh(m))
          if (!sketchMesh) {
            await this.drawMeshSketch(mesh, level, 0)
          }
        }
      }

      if (isCurrentProcessValid() && statusComplete) {
        statusComplete()
      }
    }

    const exteriorWallsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Wall,
        elementSubtype: BuildingElementSubType.Exterior,
        status: BuildingElementStatus.Complete,
      })
    }

    const interiorWallsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Wall,
        elementSubtype: BuildingElementSubType.Interior,
        status: BuildingElementStatus.Complete,
      })
    }

    const doorsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Door,
        status: BuildingElementStatus.Complete,
      })
    }

    const windowsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Window,
        status: BuildingElementStatus.Complete,
      })
    }

    const roomsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Room,
        status: BuildingElementStatus.Complete,
      })
    }

    const roofsStatusComplete = () => {
      this.updateElementStatus({
        elementType: BuildingElementType.Roof,
        status: BuildingElementStatus.Complete,
      })
    }

    const createExteriorWalls = () => {
      const storeysExterirorWalls: Mesh[] = []
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        const storeyMesh = this.createStorey(storey)
        if (storey.walls) {
          const exteriorWalls = storey.walls.filter(
            (wall) =>
              wall.type === BuildingElementSubType.Exterior ||
              wall.type === BuildingElementSubType.Unknown,
          )
          const wallMeshes = this.createWalls(exteriorWalls, storeyMesh)
          storeysExterirorWalls.push(...wallMeshes)
        }
      }
      return storeysExterirorWalls
    }

    const createInteriorWalls = () => {
      const storeysInteriorWalls: Mesh[] = []
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        let storeyMesh = this.scene?.getMeshById(storey.id) as Mesh
        if (!storeyMesh) {
          storeyMesh = this.createStorey(storey)
        }

        if (storey.walls) {
          const interiorWalls = storey.walls.filter(
            (wall) => wall.type === BuildingElementSubType.Interior,
          )
          const wallMeshes = this.createWalls(interiorWalls, storeyMesh)
          storeysInteriorWalls.push(...wallMeshes)
        }
      }
      return storeysInteriorWalls
    }

    const createDoors = () => {
      const storeysWallsDoors: Mesh[] = []
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        for (const wall of storey.walls) {
          const wallDoorsMeshes = this.createWallDoors(wall)
          if (wallDoorsMeshes) {
            storeysWallsDoors.push(...wallDoorsMeshes)
          }
        }
      }
      return storeysWallsDoors
    }

    const createWindows = () => {
      const storeysWallsWindows: Mesh[] = []
      for (let i = 0; i < building.storeys.length; i++) {
        const storey = building.storeys[i]
        for (const wall of storey.walls) {
          const wallWindowsMeshes = this.createWallWindows(wall)
          if (wallWindowsMeshes) {
            storeysWallsWindows.push(...wallWindowsMeshes)
          }
        }
      }

      return storeysWallsWindows
    }

    const createFloors = () => {
      const storeysFloors: Mesh[] = []
      for (const storey of building.storeys) {
        let storeyMesh = this.scene?.getMeshById(storey.id)
        if (!storeyMesh) {
          storeyMesh = this.createStorey(storey)
        }

        const storeyFloorsMeshes = this.createRoomsFloor(
          storey,
          storeyMesh as Mesh,
        )
        storeysFloors.push(...storeyFloorsMeshes)
      }
      return storeysFloors
    }

    const createRoofs = () => {
      const storeysRoofs: Mesh[] = []
      for (const storey of building.storeys) {
        let storeyMesh = this.scene?.getMeshById(storey.id)
        if (!storeyMesh) {
          storeyMesh = this.createStorey(storey)
        }

        const storeyBoundingVectors = storeyMesh.getHierarchyBoundingVectors(
          true,
          (mesh) =>
            !isRoofMesh(mesh) && !isSketchMesh(mesh) && !isStructuralMesh(mesh),
        )
        for (const roof of building.roofs) {
          if (roof.storey_id === storey.id) {
            const roofMesh = this.createRoof(roof)
            if (roofMesh) {
              // Temp solution to place roof on top of storey
              roofMesh.position.y = storeyBoundingVectors.max.y
              //

              roofMesh.setParent(storeyMesh)
              roofMesh.metadata = {
                ...roofMesh.metadata,
                storeyId: roof.storey_id,
                relativeToStoreyMaxPositionY:
                  roofMesh.position.y - storeyBoundingVectors.max.y,
              }
              storeysRoofs.push(roofMesh)
            }
          }
        }
      }

      return storeysRoofs
    }

    await renderElement(
      building.has_elements.exterior_walls,
      BuildingElementType.Wall,
      BuildingElementSubType.Exterior,
      0,
      createExteriorWalls,
      options?.itemIds ? undefined : exteriorWallsStatusComplete,
    )
    await renderElement(
      building.has_elements.interior_walls,
      BuildingElementType.Wall,
      BuildingElementSubType.Interior,
      0,
      createInteriorWalls,
      options?.itemIds ? undefined : interiorWallsStatusComplete,
    )
    await renderElement(
      building.has_elements.doors,
      BuildingElementType.Door,
      undefined,
      1,
      createDoors,
      options?.itemIds ? undefined : doorsStatusComplete,
    )
    await renderElement(
      building.has_elements.windows,
      BuildingElementType.Window,
      undefined,
      1,
      createWindows,
      options?.itemIds ? undefined : windowsStatusComplete,
    )
    await renderElement(
      building.has_elements.rooms,
      BuildingElementType.Room,
      undefined,
      0,
      createFloors,
      options?.itemIds ? undefined : roomsStatusComplete,
    )
    await renderElement(
      building.has_elements.roof,
      BuildingElementType.Roof,
      undefined,
      0,
      createRoofs,
      options?.itemIds ? undefined : roofsStatusComplete,
    )

    if (options && options.itemIds) {
      for (const itemId of options.itemIds) {
        const refs = getBimObjectRefId(building, itemId)
        if (refs) {
          for (const ref of refs) {
            const bimObject = store
              .getState()
              .construction.bimObjects.find(
                (object) => object.id === ref.bimobject_ref,
              )
            if (bimObject) {
              this.replaceBuildingElements([ref.id], bimObject)
            }
          }
        }
      }
    }
  }

  createStorey(storey: Storey) {
    const existingStoreyMesh = this.scene?.getMeshById(storey.id) as Mesh
    if (!!existingStoreyMesh && !existingStoreyMesh.isDisposed()) {
      return existingStoreyMesh
    }

    const storeyMesh = new Mesh('Storey', this.scene)
    storeyMesh.id = storey.id
    storeyMesh.metadata = {
      type: BuildingElementType.Storey,
    }

    return storeyMesh
  }

  createWalls(walls: Wall[], storeyMesh: Mesh) {
    const wallMeshes: Mesh[] = []
    for (const wall of walls) {
      if (wall.type !== BuildingElementSubType.Site) {
        const wallMesh = this.createWall(wall, storeyMesh)
        if (wallMesh) {
          wallMeshes.push(wallMesh)
        }
      }
    }

    return wallMeshes
  }

  createWall(wall: CreateWallOptions, storeyMesh: Mesh): Mesh {
    const {
      id,
      length,
      height,
      thickness,
      center_point,
      bottom_elevation,
      angle,
      type: subType,
    } = wall
    const existingWallMesh = this.scene?.getMeshById(id) as Mesh
    if (existingWallMesh && !existingWallMesh.isDisposed()) {
      return existingWallMesh
    }

    const wallMesh = MeshBuilder.CreateBox(
      'Wall',
      {
        width: length,
        height: height,
        depth: thickness,
        faceColors: this.faceColors,
      },
      this.scene,
    )

    wallMesh.id = id
    wallMesh.setParent(storeyMesh)
    wallMesh.position.set(
      center_point[0],
      bottom_elevation + height / 2,
      center_point[1],
    )
    wallMesh.rotation.y = deg2Rad(-angle)
    const worldMatrix = wallMesh.computeWorldMatrix(true)

    const wallDirection = Vector3.TransformNormalFromFloatsToRef(
      1,
      0,
      0,
      worldMatrix,
      new Vector3(),
    ).normalize()

    const wallNormal = Vector3.TransformNormalFromFloatsToRef(
      0,
      0,
      1,
      worldMatrix,
      new Vector3(),
    ).normalize()

    wallMesh.metadata = {
      subType,
      wallNormal,
      wallDirection,
      isHidden: false,
      hasSketch: true,
      type: BuildingElementType.Wall,
    }

    return wallMesh
  }

  createWallDoors(wall: Wall) {
    const wallMesh = this.scene?.getMeshById(wall.id) as Mesh
    if (!!!wallMesh) {
      throw new Error(
        'Error during creating wall doors: Can not add doors before wall',
      )
    }

    const doorMeshes: Mesh[] = []
    // Create and subtract holes for doors
    for (const door of wall.doors) {
      const existingDoor = this.scene?.getMeshById(door.id)
      if (existingDoor && !existingDoor.isDisposed()) {
        continue
      }

      const doorMesh = MeshBuilder.CreateBox(
        'Door',
        {
          width: door.width,
          height: Math.min(
            door.height,
            wall.height - Math.max(door.sill_height, 1e-3) - 1e-3,
          ),
          depth: wall.thickness,
          faceColors: this.faceColors,
        },
        this.scene,
      )

      doorMesh.id = door.id
      doorMesh.parent = wallMesh

      doorMesh.position.x = -wall.length / 2 + door.center_location_on_wall
      doorMesh.position.y =
        (-wall.height + door.height) / 2 + Math.max(door.sill_height, 1e-3)

      doorMesh.material = this.materialManager.getInactiveMaterial(
        BuildingElementType.Door,
      )
      doorMesh.metadata = {
        isHidden: false,
        subType: wall.type,
        isTransparent: true,
        type: BuildingElementType.Door,
        hasSketch: true,
      }
      doorMeshes.push(doorMesh)
    }

    cutOpeningHolesInWallMesh(wallMesh, doorMeshes)

    return doorMeshes
  }

  createWallWindows(wall: Wall) {
    const wallMesh = this.scene?.getMeshById(wall.id) as Mesh
    if (!!!wallMesh) {
      throw new Error(
        'Error during creating wall windows: Can not add windows before wall',
      )
    }

    const windowMeshes: Mesh[] = []
    // Create and subtract holes for windows
    for (const window of wall.windows) {
      const existingWindow = this.scene?.getMeshById(window.id)
      if (existingWindow && !existingWindow.isDisposed()) {
        continue
      }

      const windowMesh = MeshBuilder.CreateBox(
        'Window',
        {
          width: window.width,
          height: Math.min(
            window.height,
            wall.height - Math.max(window.sill_height, 1e-3) - 1e-3,
          ),
          depth: wall.thickness,
          faceColors: this.faceColors,
        },
        this.scene,
      )

      windowMesh.id = window.id
      windowMesh.parent = wallMesh

      windowMesh.position.x = -wall.length / 2 + window.center_location_on_wall
      windowMesh.position.y =
        (-wall.height + window.height) / 2 + Math.max(window.sill_height, 1e-3)

      windowMesh.material = this.materialManager.getInactiveMaterial(
        BuildingElementType.Window,
      )
      windowMesh.metadata = {
        isHidden: false,
        subType: wall.type,
        isTransparent: true,
        type: BuildingElementType.Window,
        hasSketch: true,
      }
      windowMeshes.push(windowMesh)
    }

    cutOpeningHolesInWallMesh(wallMesh, windowMeshes)

    return windowMeshes
  }

  createFloor(storey: Storey, storeyMesh: Mesh) {
    const ground = this.drawGround(storey.poly_points)
    const boundingVectors = ground.getHierarchyBoundingVectors()
    const groundHeight = boundingVectors.max.y - boundingVectors.min.y
    storeyMesh.metadata.groundHeight = isFinite(groundHeight) ? groundHeight : 0

    ground.setParent(storeyMesh)
    ground.material = this.materialManager.getNormalMaterial(
      BuildingElementType.Floor,
    )
  }

  createRoomsFloor(storey: Storey, storeyMesh: Mesh) {
    const floorMeshes: Mesh[] = []

    for (let i = 0; i < storey.rooms.length; i++) {
      const floor = storey.rooms[i].floor
      const floorMesh = this.drawGround(floor.poly_points)
      const boundingVectors = floorMesh.getHierarchyBoundingVectors()
      let floorHeight = (boundingVectors.max.y - boundingVectors.min.y) / 2
      floorHeight = isFinite(floorHeight) ? floorHeight : 0

      floorMesh.setParent(storeyMesh)
      floorMesh.position.y = floor.elevation
      floorMesh.material = this.materialManager.getNormalMaterial(
        BuildingElementType.Floor,
      )
      floorMeshes.push(floorMesh)
    }

    return floorMeshes
  }

  createRoof(roof: Roof) {
    const existingRoofMesh = this.scene?.meshes.find(
      (m) => isRoofMesh(m) && m.id === roof.id,
    )
    if (existingRoofMesh && !existingRoofMesh.isDisposed()) {
      return
    }

    const roofMesh = new Mesh('Roof', this.scene)
    roofMesh.id = roof.id
    roofMesh.metadata = {
      isHidden: false,
      relativeToStoreyMaxPositionY: 0,
      type: BuildingElementType.Roof,
    }

    const positions: number[] = []
    const indices: number[] = []

    for (const surface of roof.surfaces) {
      const surfacePositions: number[] = []
      for (const node of surface.polygon.nodes) {
        const { x, y, z } = node.point
        surfacePositions.push(x, y, z)
      }

      const surfaceIndices = earcut(surfacePositions, undefined, 3)
      indices.push(...surfaceIndices.map((i) => i + positions.length / 3))
      positions.push(...surfacePositions)
    }

    // Need to swap Y with Z coordinate
    for (let i = 1, j = 2; j < positions.length; i += 3, j += 3) {
      const swap = positions[i]
      positions[i] = positions[j]
      positions[j] = swap
    }

    const normals: number[] = []
    VertexData.ComputeNormals(positions, indices, normals)

    const vertexData = new VertexData()
    vertexData.positions = positions
    vertexData.indices = indices
    vertexData.normals = normals

    vertexData.applyToMesh(roofMesh)
    roofMesh.material = this.materialManager.getNormalMaterial(
      BuildingElementType.Roof,
    )

    const roofBoundingVectors = roofMesh.getHierarchyBoundingVectors()
    roofMesh.position.y = -roofBoundingVectors.min.y
    roofMesh.bakeCurrentTransformIntoVertices()
    roofMesh.position.y = roofBoundingVectors.min.y

    return roofMesh
  }

  activateBuildingItem(id: string) {
    this.buildingViewMode.activateBuildingItem(id)
  }

  calculateContourAndExpandGroundContour(
    center_point: number[],
    thickness: number,
    length: number,
    angle: number,
  ): Vector3[] {
    const [x, y] = center_point
    const angleRad = deg2Rad(angle)
    const dx = Math.cos(angleRad)
    const dy = Math.sin(angleRad)

    const storeysNumber = this.getStoreys().length
    if (storeysNumber === 1) {
      this.groundContour.push(
        new Vector2(x, y),
        new Vector2(x + length * dx, y + length * dy),
      )
    }

    return calculateContour(center_point, thickness, length, angle)
  }

  createLine(
    startPoint: Vector3,
    endPoint: Vector3,
    color: Color4,
    maxSteps: number,
    processId: number,
  ): Promise<Mesh> {
    return new Promise<Mesh>((lineResolve) => {
      const points: Vector3[] = []
      for (let i = 0; i <= maxSteps; i++) {
        const t = i / (maxSteps - 1)
        points.push(Vector3.Lerp(startPoint, endPoint, t))
      }

      const initialPoints: Vector3[] = []
      const colors: Color4[] = []
      for (let index = 0; index <= maxSteps; index++) {
        colors.push(color)
        initialPoints.push(startPoint)
      }

      let line = MeshBuilder.CreateLines(
        'line',
        {
          points: maxSteps === 0 ? [startPoint, endPoint] : initialPoints,
          colors: maxSteps === 0 ? [color, color] : colors,
          updatable: true,
          useVertexAlpha: false,
        },
        this.scene,
      )

      let index = 0

      const beforeRender = () => {
        if (processId !== this.currentAnimationProcessId) {
          this.scene?.unregisterBeforeRender(beforeRender)
          lineResolve(line)
          return
        }

        const current = index % (maxSteps + 1)

        const pointsArr = points.slice(0, current)
        const initialArr = initialPoints.slice(current, maxSteps + 1)

        const newPoints = initialArr.concat(pointsArr)

        line = MeshBuilder.CreateLines('line', {
          points: newPoints,
          colors: colors,
          instance: line,
          updatable: true,
          useVertexAlpha: false,
        })

        index++

        if (index > maxSteps) {
          this.scene?.unregisterBeforeRender(beforeRender)
          lineResolve(line)
        }
      }

      if (!!maxSteps) {
        this.scene?.registerBeforeRender(beforeRender)
      } else {
        lineResolve(line)
      }
    })
  }

  createLine2(
    startPoint: Vector3,
    endPoint: Vector3,
    color: Color4,
    processId: number,
    duration: number, // duration in milliseconds
  ): Promise<Mesh> {
    return new Promise<Mesh>((lineResolve) => {
      // Create the line initially invisible
      const line = MeshBuilder.CreateLines(
        'line',
        {
          points: [startPoint, endPoint],
          colors: [color, color],
          updatable: true,
          useVertexAlpha: false,
        },
        this.scene,
      )
      line.isVisible = false

      // Set a timeout to make the line visible after the specified duration
      setTimeout(() => {
        if (processId === this.currentAnimationProcessId) {
          line.isVisible = true
        }
        lineResolve(line)
      }, duration)
    })
  }

  async createSketch(
    mesh: AbstractMesh,
    level: number,
    color: Color4,
    maxSteps: number,
    processId: number,
    animationDurationPerMesh?: number,
  ): Promise<void> {
    const useEdges = true
    const meshWorldMatrix = mesh.getWorldMatrix()
    const linesToCreate: {
      startPoint: Vector3
      endPoint: Vector3
      color: Color4
    }[] = []

    if (useEdges) {
      if (!mesh.edgesRenderer) {
        mesh.enableEdgesRendering(0.9999)
        mesh.edgesWidth = 0
      }

      const linePositions = mesh.edgesRenderer?.linesPositions
      if (!linePositions) return

      for (let i = 0; i < linePositions.length; i += 12) {
        let startPoint = new Vector3(
          linePositions[i],
          linePositions[i + 1],
          linePositions[i + 2],
        )
        let endPoint = new Vector3(
          linePositions[i + 6],
          linePositions[i + 7],
          linePositions[i + 8],
        )
        Vector3.TransformCoordinatesToRef(
          startPoint,
          meshWorldMatrix,
          startPoint,
        )
        Vector3.TransformCoordinatesToRef(endPoint, meshWorldMatrix, endPoint)
        linesToCreate.push({ startPoint, endPoint, color })
      }

      mesh.disableEdgesRendering()
    } else {
      let min
      let max
      const hasChildren = mesh.getChildMeshes().length > 0
      if (!hasChildren) {
        const boundingBox = mesh.getBoundingInfo().boundingBox
        min = Vector3.TransformCoordinates(boundingBox.minimum, meshWorldMatrix)
        max = Vector3.TransformCoordinates(boundingBox.maximum, meshWorldMatrix)
      } else {
        const boundingBox = computeBoundingBox(mesh.getChildMeshes())
        min = boundingBox.min
        max = boundingBox.max
      }

      const vertices = [
        new Vector3(min.x, min.y, min.z),
        new Vector3(max.x, min.y, min.z),
        new Vector3(max.x, min.y, max.z),
        new Vector3(min.x, min.y, max.z),
        new Vector3(min.x, max.y, min.z),
        new Vector3(max.x, max.y, min.z),
        new Vector3(max.x, max.y, max.z),
        new Vector3(min.x, max.y, max.z),
      ]

      linesToCreate.push(
        ...[
          { startPoint: vertices[0], endPoint: vertices[1], color },
          { startPoint: vertices[1], endPoint: vertices[2], color },
          { startPoint: vertices[2], endPoint: vertices[3], color },
          { startPoint: vertices[3], endPoint: vertices[0], color },
          { startPoint: vertices[0], endPoint: vertices[4], color },
          { startPoint: vertices[1], endPoint: vertices[5], color },
          { startPoint: vertices[2], endPoint: vertices[6], color },
          { startPoint: vertices[3], endPoint: vertices[7], color },
          { startPoint: vertices[4], endPoint: vertices[5], color },
          { startPoint: vertices[5], endPoint: vertices[6], color },
          { startPoint: vertices[6], endPoint: vertices[7], color },
          { startPoint: vertices[7], endPoint: vertices[4], color },
        ],
      )
    }

    const lineCreationDuration = animationDurationPerMesh
      ? animationDurationPerMesh / linesToCreate.length
      : 0
    const sketchLines: Mesh[] = []
    for (const line of linesToCreate) {
      if (processId !== this.currentAnimationProcessId) {
        return
      }

      let createdLine: Mesh
      if (!!!animationDurationPerMesh) {
        createdLine = await this.createLine(
          line.startPoint,
          line.endPoint,
          line.color,
          maxSteps,
          processId,
        )
      } else {
        createdLine = await this.createLine2(
          line.startPoint,
          line.endPoint,
          line.color,
          processId,
          lineCreationDuration,
        )
      }
      sketchLines.push(createdLine)
    }

    if (processId !== this.currentAnimationProcessId) {
      return
    }

    if (!mesh.metadata) mesh.metadata = {}
    const sketchMesh = Mesh.MergeMeshes(sketchLines)
    if (sketchMesh) {
      const invertMeshWorldMatrix = meshWorldMatrix.invertToRef(new Matrix())
      sketchMesh.bakeTransformIntoVertices(invertMeshWorldMatrix)

      sketchMesh.name = 'Sketch'
      sketchMesh.isPickable = false
      sketchMesh.parent = mesh
      sketchMesh.metadata = {
        type: BuildingElementType.Sketch,
      }
    }

    if (level === 0) {
      mesh.isVisible = true
      /*mesh.metadata.sketchLines.forEach((sketchLine: number) => {
        const sketchLineMesh = this.scene?.getMeshByUniqueId(sketchLine)
        if (sketchLineMesh) {
          sketchLineMesh.isVisible = false
        }
      })*/
    } else {
      const parent = mesh.parent as AbstractMesh
      const sketchItems = parent
        .getChildMeshes()
        .filter((mesh) => mesh.metadata && mesh.metadata.hasSketch)
      const sketchDrawn =
        sketchItems.length &&
        sketchItems.every(
          (item) =>
            item.getChildMeshes().filter((mesh) => isSketchMesh(mesh)).length,
        )
      if (sketchDrawn) {
        parent.isVisible = true
        parent
          .getChildMeshes()
          .filter((mesh) => !isSketchMesh(mesh))
          .forEach((mesh) => (mesh.isVisible = true))
        /*parent.metadata.sketchLines.forEach((sketchLine: number) => {
          const sketchLineMesh = this.scene?.getMeshByUniqueId(sketchLine)
          if (sketchLineMesh) {
            sketchLineMesh.isVisible = false
          }
        })*/
        sketchItems.forEach((sketchItem) => {
          sketchItem.isVisible = true
          /*sketchItem.metadata.sketchLines.forEach((sketchLine: number) => {
            const sketchLineMesh = this.scene?.getMeshByUniqueId(sketchLine)
            if (sketchLineMesh) {
              sketchLineMesh.isVisible = false
            }
          })*/
        })
      }
    }
  }

  async drawMeshSketch(
    mesh: AbstractMesh,
    level: number,
    maxSteps: number,
    animationDurationPerMesh?: number,
  ): Promise<void> {
    if (level === 0) {
      mesh.isVisible = false
    } else {
      const parent = mesh.parent as AbstractMesh
      parent
        .getChildMeshes()
        .filter((mesh) => !isSketchMesh(mesh))
        .forEach((mesh) => (mesh.isVisible = false))
    }

    let color
    if (isWindowMesh(mesh)) {
      color = Color4.FromHexString('#0080FF')
    } else if (isDoorMesh(mesh)) {
      color = Color4.FromHexString('#DD9E23')
    } else if (isRoofMesh(mesh)) {
      color = Color4.FromHexString('#223b05')
    } else {
      color = Color4.FromHexString('#000000')
    }

    const processId = this.currentAnimationProcessId
    await this.createSketch(
      mesh,
      level,
      color,
      maxSteps,
      processId,
      animationDurationPerMesh,
    )
  }

  showSketchAnimation(
    meshes: AbstractMesh[],
    level: number,
    animationDurationPerMesh?: number,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const processId = ++this.currentAnimationProcessId
      const maxSteps = 3

      const drawMeshesSequentially = async () => {
        for (const mesh of meshes) {
          if (processId !== this.currentAnimationProcessId) {
            resolve()
            return
          }
          await this.drawMeshSketch(
            mesh,
            level,
            maxSteps,
            animationDurationPerMesh,
          )
        }
        resolve()
      }

      drawMeshesSequentially()
    })
  }

  createStructuralWalls(
    wallMesh: Mesh,
    structuralWalls: (ShearWall | LoadBearingWall)[],
    storeyMesh: Mesh,
    structuralType: BuildingStructuralTypes,
  ) {
    if (structuralWalls.length === 0) {
      return
    }

    const wallCSG = CSG.FromMesh(wallMesh)
    for (const structuralWall of structuralWalls) {
      const existingStructuralWall = this.scene?.getMeshById(structuralWall.id)
      if (existingStructuralWall && !existingStructuralWall.isDisposed()) {
        continue
      }

      let structuralWallMesh = MeshBuilder.CreateBox(
        structuralType.split(' ').join(''),
        {
          width: structuralWall.length,
          height: structuralWall.height,
          depth: structuralWall.height,
        },
        this.scene,
      )
      structuralWallMesh.position.set(
        structuralWall.center_point[0],
        wallMesh.position.y,
        structuralWall.center_point[1],
      )
      structuralWallMesh.rotation.y = deg2Rad(structuralWall.angle)
      structuralWallMesh.computeWorldMatrix(true)

      const structuralWallCSG = CSG.FromMesh(structuralWallMesh)
      structuralWallMesh.dispose()

      structuralWallCSG.intersectInPlace(wallCSG)
      if (
        (structuralWallCSG as any)._polygons &&
        !(structuralWallCSG as any)._polygons.length
      ) {
        console.warn('Error while rendering structural wall', {
          structuralWallId: structuralWall.id,
          wallId: structuralWall.wall_id,
          structuralType,
        })
        continue
      }

      const isEngineeringPageActive =
        store.getState().construction.selectedConstructionPage ===
        ConstructionPage.Engineering
      const animationComplete =
        store.getState().structural.structuralAnimationStatus ===
        StructuralAnimationStatus.Completed
      structuralWallMesh = structuralWallCSG.toMesh(
        structuralType.split(' ').join(''),
        this.materialManager.getStructuralMaterial(structuralType),
        this.scene,
      )
      structuralWallMesh.removeVerticesData(VertexBuffer.ColorKind)

      structuralWallMesh.id = structuralWall.id
      structuralWallMesh.isVisible =
        isEngineeringPageActive && animationComplete
      structuralWallMesh.isPickable =
        isEngineeringPageActive && animationComplete

      structuralWallMesh.setParent(storeyMesh)
      structuralWallMesh.position.set(
        structuralWall.center_point[0],
        wallMesh.position.y,
        structuralWall.center_point[1],
      )
      structuralWallMesh.rotation.y = deg2Rad(-structuralWall.angle)
      structuralWallMesh.computeWorldMatrix(true)

      structuralWallMesh.metadata = {
        wallId: structuralWall.wall_id,
        type: BuildingElementType.Structural,
        structuralType,
      }
    }
  }

  enableWallTransparencyMode() {
    this.scene?.registerBeforeRender(this.transparencyFunction)
  }

  disableWallTransparencyMode() {
    this.scene?.unregisterBeforeRender(this.transparencyFunction)
    this.scene?.meshes
      .filter(
        (m) => isWallMesh(m) && !m.metadata.codeValidationStatus && m.isVisible,
      )
      .forEach((wallMesh) => {
        if (
          !wallMesh.material?.metadata ||
          (wallMesh.material!.metadata &&
            !wallMesh.material!.metadata.overrideTransparency)
        ) {
          wallMesh.metadata.isTransparent = false
          wallMesh.material = this.materialManager.getNormalMaterial(
            BuildingElementType.Wall,
          )
        }
      })
  }

  changeStoreyElementVisibility(hiddenElements: StoreyElementsVisibility) {
    const selectedStoreyId = store.getState().construction.selectedStoreyId
    const isRoofsHidden =
      !store.getState().construction.hideShowOptions[
        HideShowOption.RoofsVisibility
      ]
    this.scene?.meshes
      // Filter only meshes that are present in the Layer List
      .filter(isSelectableMesh)
      .forEach((mesh) => {
        if (hiddenElements[mesh.id] === undefined) {
          return
        }

        // Change visibility only if
        // 'Entire Home' is selected OR
        // mesh is a part of selected storey
        if (
          (isRoofMesh(mesh) || isWallMesh(mesh)) &&
          (!selectedStoreyId || selectedStoreyId === mesh.parent?.id)
        ) {
          if (isRoofMesh(mesh)) {
            mesh.isVisible = !hiddenElements[mesh.id] && !isRoofsHidden
            mesh
              .getChildMeshes(
                true,
                (child) => child instanceof AbstractMesh && isSketchMesh(child),
              )
              .forEach(
                (child) =>
                  (child.isVisible =
                    !hiddenElements[mesh.id] && !isRoofsHidden),
              )
          } else {
            mesh.isVisible = !hiddenElements[mesh.id]
            mesh
              .getChildMeshes(
                true,
                (child) => child instanceof AbstractMesh && isSketchMesh(child),
              )
              .forEach((child) => {
                child.isVisible = !hiddenElements[mesh.id]
              })
          }
        }

        if (isDoorMesh(mesh) || isWindowMesh(mesh)) {
          const wallMesh = mesh.parent
          // For Door and Windows meshes get storey id using wall mesh parent id
          if (!selectedStoreyId || selectedStoreyId === wallMesh?.parent?.id) {
            mesh.isVisible = !hiddenElements[mesh.id]
            mesh
              .getChildMeshes(
                true,
                (child) => child instanceof AbstractMesh && isSketchMesh(child),
              )
              .forEach((child) => {
                child.isVisible = !hiddenElements[mesh.id]
              })
          }
        }

        mesh.metadata.isHidden = hiddenElements[mesh.id]
      })
  }

  toggleGridVisibility(isVisible: boolean) {
    const gridMesh = this.scene?.getMeshByName('Grid')
    if (!gridMesh) {
      return
    }

    gridMesh.isVisible = isVisible
  }

  toggleRoofVisibility(isVisible: boolean) {
    const selectedStoreyId = store.getState().construction.selectedStoreyId
    this.scene?.meshes
      .filter((m) => isRoofMesh(m))
      .forEach((roofMesh) => {
        // Set visibility only when roof is not hidden AND
        // When 'Entire Home' is selected OR
        // When roof is a part of selected storey
        if (
          !roofMesh.metadata.isHidden &&
          (!selectedStoreyId || selectedStoreyId === roofMesh.parent?.id)
        ) {
          roofMesh.isVisible = isVisible

          roofMesh.getChildMeshes(true).forEach((mesh) => {
            if (isSketchMesh(mesh)) {
              mesh.isVisible = isVisible
            }
          })
        }
      })
  }

  private shadowsBeforeRenderFunction?: () => void
  enableShadows() {
    const scene = this.scene
    if (!scene) {
      console.warn('Error while enabling shadows: Scene is undefined')
      return
    }

    for (const light of scene.lights) {
      if (light.getShadowGenerator() && light.name === 'ShadowLight') {
        light.getShadowGenerator()?.dispose()
        light.dispose()

        const fakeGround = scene.getMeshByName('shadowOnlyFloor')
        if (fakeGround) {
          scene.removeMesh(fakeGround)
          fakeGround.dispose(true, true)
        }

        if (this.shadowsBeforeRenderFunction) {
          scene.unregisterBeforeRender(this.shadowsBeforeRenderFunction)
        }
      }
    }

    const angle = Math.PI / 2
    const rotationMatrix = Matrix.RotationY(angle)
    this.shadowsBeforeRenderFunction = () => {
      const camera = this.camera
      if (!camera) {
        return
      }

      const cameraPosition = camera.globalPosition
      const cameraTarget = camera.target
      cameraTarget
        .subtractToRef(cameraPosition, directionalLight.direction)
        .normalize()

      Vector3.TransformNormalToRef(
        directionalLight.direction,
        rotationMatrix,
        directionalLight.direction,
      )

      const cameraRadius = Math.min(camera.radius, diameter)
      directionalLight.position.copyFrom(cameraTarget)
      directionalLight.direction.scaleAndAddToRef(
        -cameraRadius,
        directionalLight.position,
      )
      directionalLight.position.y = Math.max(
        directionalLight.position.y,
        maxHeight,
      )

      cameraTarget
        .subtractToRef(directionalLight.position, directionalLight.direction)
        .normalize()
    }

    const directionalLight = new DirectionalLight(
      'ShadowLight',
      new Vector3(0, -1, 0),
      scene,
    )
    directionalLight.intensity = 0.1

    const shadowGenerator = new ShadowGenerator(2048, directionalLight)
    shadowGenerator.bias = 0.001
    shadowGenerator.normalBias = 0.02
    shadowGenerator.usePercentageCloserFiltering = true
    shadowGenerator.setDarkness(0)

    scene.meshes
      .filter((m) => isGroundMesh(m) || isWallMesh(m))
      .forEach((mesh) => {
        if (isWallMesh(mesh)) {
          shadowGenerator.addShadowCaster(mesh)
        }

        mesh.receiveShadows = true
      })

    const minVector = new Vector3(
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
    )
    const maxVector = new Vector3(
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    )

    const floorAndWallMeshes = scene.meshes.filter(
      (m) => m.isVisible && (isGroundMesh(m) || isWallMesh(m)),
    )
    if (floorAndWallMeshes.length) {
      for (const mesh of floorAndWallMeshes) {
        const boundingInfo = mesh.getBoundingInfo()
        const boundingBox = boundingInfo.boundingBox

        minVector.minimizeInPlace(boundingBox.minimumWorld)
        maxVector.maximizeInPlace(boundingBox.maximumWorld)
      }

      const groundLength = maxVector.x - minVector.x
      const groundWidth = maxVector.z - minVector.z
      const depth = 5
      const fakeGround = MeshBuilder.ExtrudePolygon(
        'shadowOnlyFloor',
        {
          shape: [
            new Vector3(-2 * groundLength, 0, -2 * groundWidth),
            new Vector3(-2 * groundLength, 0, 2 * groundWidth),
            new Vector3(2 * groundLength, 0, 2 * groundWidth),
            new Vector3(2 * groundLength, 0, -2 * groundWidth),
          ],
          depth,
        },
        scene,
        earcut,
      )
      fakeGround.position.y = depth
      fakeGround.isPickable = false
      fakeGround.bakeCurrentTransformIntoVertices()
      const commonGroundBBox = new BoundingBox(minVector, maxVector)
      fakeGround.position = new Vector3(
        commonGroundBBox.centerWorld.x,
        commonGroundBBox.minimumWorld.y - depth - 0.1,
        commonGroundBBox.centerWorld.z,
      )
      const shadowOnlyMaterial = new ShadowOnlyMaterial('shadowOnly', scene)
      shadowOnlyMaterial.shadowColor = Color3.FromHexString('#DDDDDD')
      shadowOnlyMaterial.activeLight = directionalLight
      fakeGround.material = shadowOnlyMaterial
      fakeGround.receiveShadows = true
      fakeGround.alphaIndex = 0
    }

    const buildingBBox = getBuildingBoundingBox()
    const maxHeight = buildingBBox ? buildingBBox.max.y - buildingBBox.min.y : 0
    const diameter = buildingBBox
      ? Vector3.Distance(buildingBBox.max, buildingBBox.min)
      : 0

    scene.registerBeforeRender(this.shadowsBeforeRenderFunction)
  }

  createStructuralElements(building: Building, options?: { itemId?: string }) {
    if (options && options.itemId) {
      const elementToRemove = this.scene?.getMeshById(options.itemId)
      if (elementToRemove) {
        this.removeBuildingElement(elementToRemove)
      }
    }

    this.updateElementStatus({
      elementType: BuildingElementType.Structural,
      status: BuildingElementStatus.Running,
    })
    for (const storey of building.storeys) {
      const storeyMesh = this.scene?.getMeshById(storey.id) as Mesh
      if (!storeyMesh) {
        console.warn(`Storey mesh with id ${storey.id} not found`)
        continue
      }

      for (const wall of storey.walls) {
        const wallMesh = this.scene?.getMeshById(wall.id) as Mesh
        if (!wallMesh) {
          console.warn(`Wall mesh with id ${wall.id} not found`)
          continue
        }

        const loadBearingWalls = storey.load_bearing_walls.filter(
          (loadBearingWall) => loadBearingWall.wall_id === wall.id,
        )
        this.createStructuralWalls(
          wallMesh,
          loadBearingWalls,
          storeyMesh,
          BuildingStructuralTypes.LoadBearingWall,
        )

        const shearWalls = storey.selected_shear_walls.filter(
          (shearWall) => shearWall.wall_id === wall.id,
        )
        this.createStructuralWalls(
          wallMesh,
          shearWalls,
          storeyMesh,
          BuildingStructuralTypes.ShearWall,
        )

        this.createRectangleFramingElement(
          wall.studs,
          BuildingStructuralTypes.Stud,
          RectangleFramingElementType.Stud,
          wallMesh,
        )

        for (const window of wall.windows) {
          const windowMesh = this.scene?.getMeshById(window.id) as Mesh
          if (!windowMesh) {
            console.warn(`Window mesh with id ${window.id} not found`)
            continue
          }

          this.createRectangleFramingElement(
            window.headers,
            BuildingStructuralTypes.Header,
            RectangleFramingElementType.Header,
            windowMesh,
          )

          for (const header of window.headers) {
            const headerMesh = this.scene?.getMeshById(header.id) as Mesh
            if (!headerMesh) {
              console.warn(`Header mesh with id ${header.id} not found`)
              continue
            }

            if (header.posts) {
              this.createRectangleFramingElement(
                header.posts,
                BuildingStructuralTypes.Header,
                RectangleFramingElementType.Post,
                headerMesh,
              )
            }
          }

          this.createRectangleFramingElement(
            window.studs,
            BuildingStructuralTypes.Stud,
            RectangleFramingElementType.Stud,
            windowMesh,
          )
        }

        for (const door of wall.doors) {
          const doorMesh = this.scene?.getMeshById(door.id) as Mesh
          if (!doorMesh) {
            console.warn(`Door mesh with id ${door.id} not found`)
            continue
          }

          this.createRectangleFramingElement(
            door.headers,
            BuildingStructuralTypes.Header,
            RectangleFramingElementType.Header,
            doorMesh,
          )

          for (const header of door.headers) {
            const headerMesh = this.scene?.getMeshById(header.id) as Mesh
            if (!headerMesh) {
              console.warn(`Header mesh with id ${header.id} not found`)
              continue
            }

            if (header.posts) {
              this.createRectangleFramingElement(
                header.posts,
                BuildingStructuralTypes.Header,
                RectangleFramingElementType.Post,
                headerMesh,
              )
            }
          }

          this.createRectangleFramingElement(
            door.studs,
            BuildingStructuralTypes.Stud,
            RectangleFramingElementType.Stud,
            doorMesh,
          )
        }
      }

      this.createRectangleFramingElement(
        storey.beams,
        BuildingStructuralTypes.Beam,
        RectangleFramingElementType.Beam,
        storeyMesh,
      )

      for (const beam of storey.beams) {
        const beamMesh = this.scene?.getMeshById(beam.id) as Mesh
        if (!beamMesh) {
          console.warn(`Beam mesh with id ${beam.id} not found`)
          continue
        }

        if (beam.posts) {
          this.createRectangleFramingElement(
            beam.posts,
            BuildingStructuralTypes.Beam,
            RectangleFramingElementType.Post,
            beamMesh,
          )
        }
      }

      for (const span of storey.spans) {
        let existingSpanMesh = this.scene?.getMeshById(span.id) as Mesh
        if (!(existingSpanMesh && !existingSpanMesh.isDisposed())) {
          const spanMesh = new Mesh(
            BuildingElementType.Span,
            this.scene,
            storeyMesh,
          )

          spanMesh.id = span.id
          spanMesh.metadata = {
            type: BuildingElementType.Span,
          }
          existingSpanMesh = spanMesh
        }

        this.createRectangleFramingElement(
          span.joists,
          BuildingStructuralTypes.Joist,
          RectangleFramingElementType.Joist,
          existingSpanMesh,
        )
      }
    }

    for (const roof of building.roofs) {
      const roofMesh = this.scene?.getMeshById(roof.id) as Mesh
      if (!roofMesh) {
        console.warn(`Storey mesh with id ${roof.id} not found`)
        continue
      }

      const rafters = roof.surfaces.flatMap((surface) => surface.rafters)
      this.createRectangleFramingElement(
        rafters,
        BuildingStructuralTypes.Roof,
        RectangleFramingElementType.Rafter,
        roofMesh,
      )

      this.createRectangleFramingElement(
        roof.ridges,
        BuildingStructuralTypes.Roof,
        RectangleFramingElementType.Ridge,
        roofMesh,
      )

      for (const ridge of roof.ridges) {
        const ridgeMesh = this.scene?.getMeshById(ridge.id) as Mesh
        if (!ridgeMesh) {
          console.warn(`Ridge mesh with id ${ridge.id} not found`)
          continue
        }

        if (ridge.posts) {
          this.createRectangleFramingElement(
            ridge.posts,
            BuildingStructuralTypes.Roof,
            RectangleFramingElementType.Post,
            ridgeMesh,
          )
        }
      }

      this.createRectangleFramingElement(
        roof.hips,
        BuildingStructuralTypes.Roof,
        RectangleFramingElementType.Hip,
        roofMesh,
      )
    }

    if (building.foundations) {
      for (const foundation of building.foundations) {
        this.createFoundation(foundation)
      }
    }

    this.scene?.registerBeforeRender(() => {
      const structuralInstances = this.scene?.meshes.filter(
        (m) =>
          isStructuralMesh(m) && m.edgesRenderer && m instanceof InstancedMesh,
      )
      structuralInstances?.forEach((instance) => {
        instance.edgesWidth =
          (instance as InstancedMesh).sourceMesh?.material?.alpha! > 0.99
            ? 40
            : 0
      })
    })

    this.updateElementStatus({
      elementType: BuildingElementType.Structural,
      status: BuildingElementStatus.Complete,
    })
  }

  async createBimObjects(building: Building, bimObjects: BimObject[]) {
    if (!bimObjects.length) {
      return
    }

    const bimObjectsMap = buildBimObjectRefIdMap(building)
    await Promise.all(
      Array.from(bimObjectsMap.entries()).map(async ([key, value]) => {
        const bimObject = bimObjects.find((bimObject) => bimObject.id === key)
        if (!bimObject) {
          console.error('Missing building BIM object, id: ', key)
        } else {
          await this.replaceBuildingElements(Array.from(value), bimObject)
        }
      }),
    )
  }

  testStructuralElements(
    building: Building,
    options?: {
      itemId?: string
      itemIds?: string[]
      clearStructuralElements: boolean
    },
  ) {
    this.scene?.meshes.forEach((mesh) => {
      if (
        isStructuralMesh(mesh) &&
        mesh.metadata?.structuralType === BuildingStructuralTypes.Beam
      ) {
        console.log(
          'isStructuralMesh(mesh)',
          isStructuralMesh(mesh),
          mesh.metadata?.structuralType,
        )
        mesh.isVisible = false
      }
    })
    this.selectionManager.deselect()
  }

  hasStructuralMeshes() {
    return this.scene?.meshes.find((mesh) => isStructuralMesh(mesh))
  }

  clearStructuralElements() {
    do {
      console.log('before', this.scene?.meshes.length)
      this.scene?.meshes.forEach((mesh) => {
        if (isStructuralMesh(mesh)) {
          console.log(
            'isStructuralMesh(mesh)',
            isStructuralMesh(mesh),
            mesh.metadata?.structuralType,
          )
          mesh.id = ''
          mesh.actionManager?.dispose()
          this.scene?.removeMesh(mesh, true)
          mesh.dispose()
        }
      })
      console.log('after', this.scene?.meshes.length)
    } while (this.hasStructuralMeshes())
  }

  async playStructuralFadeAnimation() {
    const playAnimation = async (animationGroup: AnimationGroup) => {
      animationGroup.play()
      return new Promise<void>((resolve) => {
        animationGroup.onAnimationEndObservable.addOnce(() => {
          resolve()
        })
      })
    }

    const structuralAnimationGroups = getEnums(BuildingStructuralTypes).reduce<
      AnimationGroup[]
    >((animationGroups, [key, value]) => {
      const structuralAnimationGroup = this.scene?.getAnimationGroupByName(
        `${key}FadeAnimationGroup`,
      )

      const structuralMeshes = this.scene?.meshes.filter(
        (mesh) =>
          isStructuralMesh(mesh) && mesh.metadata.structuralType === value,
      )
      if (structuralAnimationGroup && !!structuralMeshes?.length) {
        animationGroups.push(structuralAnimationGroup)
      }

      return animationGroups
    }, [])

    for (const animationGroup of structuralAnimationGroups) {
      await playAnimation(animationGroup)
      // const structuralMeshes = this.scene?.meshes.filter(
      //   (mesh) =>
      //     isStructuralMesh(mesh) &&
      //     mesh.metadata.structuralType ===
      //       animationGroup.metadata.structuralType,
      // )
      // structuralMeshes?.forEach(mesh => {
      //   if ([BuildingStructuralTypes.Beam, BuildingStructuralTypes.Header, BuildingStructuralTypes.Joist].includes(animationGroup.metadata.structuralType)) {
      //     if (!mesh.edgesRenderer) {
      //       mesh.enableEdgesRendering(0.9999)
      //     }

      //     mesh.edgesColor = Color4.FromHexString('#1D1C1F')
      //     mesh.edgesWidth = 40
      //   }
      // })
    }
  }

  async playCodeValidationAnimation(data: CodeValidationRuleStatus[]) {
    const itemTypes = Array.from(
      data.reduce<Set<BuildingElementType>>((arr, current) => {
        const mesh = this.scene?.getMeshById(current.elementId)
        const meshType: BuildingElementType = mesh?.metadata?.type
        if (mesh && meshType) {
          arr.add(meshType)
        }

        return arr
      }, new Set()),
    ).sort()

    const messages = itemTypes.map(
      (itemType) => `Validating ${BuildingElementType[itemType]}s...`,
    )

    store.dispatch(setCurrentValildationMessages(messages))

    for (const item of data) {
      const selectedConstructionTab =
        store.getState().construction.selectedConstructionTab
      if (
        selectedConstructionTab !== ConstructionTab.Validation ||
        !this.scene
      ) {
        break
      }

      const mesh = this.scene?.getMeshById(item.elementId)
      const meshType: BuildingElementType = mesh?.metadata?.type
      const meshCodeValidationStatus: CodeValidationStatus =
        mesh?.metadata?.codeValidationStatus

      if (
        !mesh ||
        !meshType ||
        meshCodeValidationStatus === CodeValidationStatus.Failed
      ) {
        continue
      }

      mesh.metadata.codeValidationStatus = item.status
      mesh.metadata.codeValidationDiscipline = item.discipline

      if (!(mesh instanceof InstancedMesh)) {
        switch (item.status) {
          case CodeValidationStatus.Passed:
            mesh.material = this.materialManager.getAcceptedMaterial(meshType)
            break
          case CodeValidationStatus.Failed:
            mesh.material = this.materialManager.getCriticalMaterial(meshType)
            break
        }

        if (item.status === CodeValidationStatus.Passed) {
          this.scene.onBeforeRenderObservable.addOnce(async () => {
            await delay(100)
            mesh.material = mesh.metadata.isTransparent
              ? this.materialManager.getInactiveMaterial(meshType)
              : this.materialManager.getNormalMaterial(meshType)
          })
        }
      }

      await delay(50)
    }

    this.sceneManager
      .getEventEmitter()
      ?.emit(EventType.CodeValidationAnimationFinished)
  }

  removeBuildingElement(elementToDelete: AbstractMesh) {
    this.selectionManager.deselect()

    if (!isStructuralMesh(elementToDelete)) {
      const wallMesh = elementToDelete.parent as Mesh
      if (wallMesh && isWallMesh(wallMesh)) {
        const boundingBox = wallMesh.getBoundingInfo().boundingBox
        const updatedWallMesh = MeshBuilder.CreateBox(
          'Wall',
          {
            width: boundingBox.extendSize.x * 2,
            height: boundingBox.extendSize.y * 2,
            depth: boundingBox.extendSize.z * 2,
            faceColors: this.faceColors,
          },
          this.scene,
        )
        updatedWallMesh.id = wallMesh.id
        updatedWallMesh.name = wallMesh.name
        updatedWallMesh.setParent(wallMesh.parent)
        updatedWallMesh.scaling.copyFrom(wallMesh.scaling)
        updatedWallMesh.rotation.copyFrom(wallMesh.rotation)
        updatedWallMesh.position.copyFrom(wallMesh.position)
        updatedWallMesh.computeWorldMatrix()
        this.drawMeshSketch(updatedWallMesh, 0, 0)
        const openingMeshes = wallMesh
          .getChildMeshes(
            true,
            (node): node is Mesh =>
              node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
          )
          .filter((m) => m.id !== elementToDelete.id)
        cutOpeningHolesInWallMesh(updatedWallMesh, openingMeshes)

        wallMesh.getChildMeshes(true).forEach((child) => {
          if (!isSketchMesh(child)) {
            child.setParent(updatedWallMesh)
          }
        })

        updatedWallMesh.metadata = wallMesh.metadata
        wallMesh.metadata = null

        this.addItemSelection(updatedWallMesh)
        this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
          if (filterFun(updatedWallMesh)) {
            updatedWallMesh.actionManager?.registerAction(action)
          }
        })

        wallMesh.id = ''
        wallMesh.actionManager?.dispose()
        this.scene?.removeMesh(wallMesh, true)
        wallMesh.dispose()
      }
    }

    elementToDelete.id = ''
    this.scene?.removeMesh(elementToDelete, true)
    if (isBimObjectMesh(elementToDelete)) {
      elementToDelete
        .getChildMeshes()
        .forEach((m) => m.actionManager?.dispose())
      elementToDelete.dispose()
    } else {
      elementToDelete.actionManager?.dispose()
      elementToDelete.dispose()
    }

    if (isWallMesh(elementToDelete)) {
      this.scene?.meshes
        .filter(
          (mesh) =>
            isStructuralMesh(mesh) &&
            mesh.metadata.wallId === elementToDelete.id,
        )
        .forEach((mesh) => {
          mesh.actionManager?.dispose()
          this.scene?.removeMesh(mesh, true)
          mesh.dispose()
        })
    }
  }

  snapTwoWallsElements({ firstMesh, secondMesh }: SnapWallElementsPayload) {
    this.selectionManager.deselect()

    const firstMeshWallDirection = firstMesh.metadata?.wallDirection as Vector3
    const secondMeshWallDirection = secondMesh.metadata
      ?.wallDirection as Vector3
    if (!firstMeshWallDirection || !secondMeshWallDirection) {
      console.error('First Or/And Second Walls Direction are undefined', {
        firstMeshWallDirection,
        secondMeshWallDirection,
      })
      return
    }

    const firstMeshBoundingBox = firstMesh.getBoundingInfo().boundingBox
    const {
      x: firstMeshHalfLength,
      y: firstMeshHalfHeight,
      z: firstMeshHalfThickness,
    } = firstMeshBoundingBox.extendSize
    const secondMeshBoundingBox = secondMesh.getBoundingInfo().boundingBox
    const {
      x: secondMeshHalfLength,
      y: secondMeshHalfHeight,
      z: secondMeshHalfThickness,
    } = secondMeshBoundingBox.extendSize

    let { t, s } =
      lineToLine2DIntersection(
        firstMesh.position,
        firstMeshWallDirection,
        secondMesh.position,
        secondMeshWallDirection,
      ) ?? {}

    if (t && s) {
      if (
        Math.abs(t) + firstMeshHalfLength >
        Math.abs(s) + secondMeshHalfLength
      ) {
        t += t > 0 ? secondMeshHalfThickness : -secondMeshHalfThickness
        s += s > 0 ? -firstMeshHalfThickness : firstMeshHalfThickness
      } else {
        t += t > 0 ? -secondMeshHalfThickness : secondMeshHalfThickness
        s += s > 0 ? firstMeshHalfThickness : -firstMeshHalfThickness
      }
    } else {
      const tmpDirection = secondMesh.position.subtractToRef(
        firstMesh.position,
        TmpVectors.Vector3[0],
      )
      tmpDirection.y = 0
      const coeffStart =
        tmpDirection.dot(firstMeshWallDirection) + secondMeshHalfLength
      const coeffEnd =
        tmpDirection.dot(firstMeshWallDirection) - secondMeshHalfLength
      const isSameDirection = isParallel(
        firstMeshWallDirection,
        secondMeshWallDirection,
      )

      if (firstMeshHalfLength < coeffStart && firstMeshHalfLength < coeffEnd) {
        const distanceBetweenWalls =
          Math.min(coeffStart, coeffEnd) - firstMeshHalfLength
        t = firstMeshHalfLength + distanceBetweenWalls / 2
        s = secondMeshHalfLength + distanceBetweenWalls / 2
        if (isSameDirection) {
          s = -s
        }
      } else if (
        -firstMeshHalfLength > coeffStart &&
        -firstMeshHalfLength > coeffEnd
      ) {
        const distanceBetweenWalls =
          firstMeshHalfLength - Math.max(coeffStart, coeffEnd)
        t = -(firstMeshHalfLength + distanceBetweenWalls / 2)
        s = -(secondMeshHalfLength + distanceBetweenWalls / 2)
        if (isSameDirection) {
          s = -s
        }
      } else {
        return
      }
    }

    const startPoint = firstMesh.position.clone()
    const centerPoint = new Vector3()
    const endPoint = firstMesh.position.clone()
    if (Math.abs(t) > firstMeshHalfLength) {
      // t will be on end point
      if (t > 0) {
        firstMeshWallDirection.scaleAndAddToRef(
          -firstMeshHalfLength,
          startPoint,
        )
        firstMeshWallDirection.scaleAndAddToRef(t, endPoint)
      } else {
        firstMeshWallDirection.scaleAndAddToRef(t, startPoint)
        firstMeshWallDirection.scaleAndAddToRef(firstMeshHalfLength, endPoint)
      }
      const newLength = Vector3.Distance(startPoint, endPoint)
      Vector3.CenterToRef(startPoint, endPoint, centerPoint)

      const wallId = firstMesh.id
      const wallMeshParent = firstMesh.parent
      const wallSubType = firstMesh.metadata.subType
      const wallMaterial = firstMesh.material
      const wallStructuralMeshes = firstMesh
        .getChildMeshes(
          true,
          (mesh) => mesh instanceof AbstractMesh && isStructuralMesh(mesh),
        )
        .map((mesh) => mesh.setParent(null))
      const wallOpeningsMeshes = firstMesh
        .getChildMeshes(
          true,
          (mesh) =>
            mesh instanceof AbstractMesh &&
            (isWindowMesh(mesh) || isDoorMesh(mesh)),
        )
        .map((mesh) => mesh.setParent(null))

      const center_point = [centerPoint.x, centerPoint.z]
      const angle = rad2Deg(firstMesh.rotation.y)
      const bottom_elevation = firstMesh.position.y - firstMeshHalfHeight

      firstMesh.dispose()

      const wall: CreateWallOptions = {
        id: wallId,
        type: wallSubType,
        length: newLength,
        height: firstMeshHalfHeight * 2,
        thickness: firstMeshHalfThickness * 2,
        bottom_elevation,
        center_point,
        angle,
      }

      let newWallMesh = this.createWall(wall, wallMeshParent as Mesh)
      const newWallMeshMetadata = { ...newWallMesh.metadata }
      const newWallMeshCSG = CSG.FromMesh(newWallMesh)
      newWallMesh.dispose()

      wallOpeningsMeshes.forEach((child) => {
        const childCSG = CSG.FromMesh(child as Mesh)
        newWallMeshCSG.subtractInPlace(childCSG)
      })

      newWallMesh = newWallMeshCSG.toMesh('Wall', wallMaterial, this.scene)
      newWallMesh.id = wall.id
      newWallMesh.setParent(wallMeshParent)
      wallOpeningsMeshes.forEach((m) => m.setParent(newWallMesh))
      wallStructuralMeshes.forEach((m) => m.setParent(newWallMesh))
      newWallMesh.metadata = newWallMeshMetadata

      this.addItemSelection(newWallMesh)
      this.drawMeshSketch(newWallMesh, 0, 0)

      this.sceneManager
        .getEventEmitter()
        ?.emit(EventType.BuildingEditWallElement, {
          id: wall.id,
          length: wall.length,
          center_point: wall.center_point,
          anchor: t < 0 ? WallAnchor.End : WallAnchor.Start,
        })

      setTimeout(() => createWallGaps(), 100)
    }

    startPoint.copyFrom(secondMesh.position)
    endPoint.copyFrom(secondMesh.position)
    if (Math.abs(s) > secondMeshHalfLength) {
      // s will be on end point
      if (s > 0) {
        secondMeshWallDirection.scaleAndAddToRef(
          -secondMeshHalfLength,
          startPoint,
        )
        secondMeshWallDirection.scaleAndAddToRef(s, endPoint)
      } else {
        secondMeshWallDirection.scaleAndAddToRef(s, startPoint)
        secondMeshWallDirection.scaleAndAddToRef(secondMeshHalfLength, endPoint)
      }
      const newLength = Vector3.Distance(startPoint, endPoint)
      Vector3.CenterToRef(startPoint, endPoint, centerPoint)

      const wallId = secondMesh.id
      const wallMeshParent = secondMesh.parent
      const wallSubType = secondMesh.metadata.subType
      const wallMaterial = secondMesh.material
      const wallStructuralMeshes = secondMesh
        .getChildMeshes(
          true,
          (mesh) => mesh instanceof AbstractMesh && isStructuralMesh(mesh),
        )
        .map((mesh) => mesh.setParent(null))
      const wallOpeningsMeshes = secondMesh
        .getChildMeshes(
          true,
          (mesh) =>
            mesh instanceof AbstractMesh &&
            (isWindowMesh(mesh) || isDoorMesh(mesh)),
        )
        .map((mesh) => mesh.setParent(null))

      const center_point = [centerPoint.x, centerPoint.z]
      const angle = rad2Deg(secondMesh.rotation.y)
      const bottom_elevation = secondMesh.position.y - secondMeshHalfHeight

      secondMesh.dispose()

      const wall: CreateWallOptions = {
        id: wallId,
        type: wallSubType,
        length: newLength,
        height: secondMeshHalfHeight * 2,
        thickness: secondMeshHalfThickness * 2,
        bottom_elevation,
        center_point,
        angle,
      }

      let newWallMesh = this.createWall(wall, wallMeshParent as Mesh)
      const newWallMeshMetadata = { ...newWallMesh.metadata }
      const newWallMeshCSG = CSG.FromMesh(newWallMesh)
      newWallMesh.dispose()

      wallOpeningsMeshes.forEach((child) => {
        const childCSG = CSG.FromMesh(child as Mesh)
        newWallMeshCSG.subtractInPlace(childCSG)
      })

      newWallMesh = newWallMeshCSG.toMesh('Wall', wallMaterial, this.scene)
      newWallMesh.id = wall.id
      newWallMesh.setParent(wallMeshParent)
      wallOpeningsMeshes.forEach((m) => m.setParent(newWallMesh))
      wallStructuralMeshes.forEach((m) => m.setParent(newWallMesh))
      newWallMesh.metadata = newWallMeshMetadata

      this.addItemSelection(newWallMesh)
      this.drawMeshSketch(newWallMesh, 0, 0)

      this.sceneManager
        .getEventEmitter()
        ?.emit(EventType.BuildingEditWallElement, {
          id: wall.id,
          length: wall.length,
          center_point: wall.center_point,
          anchor: s < 0 ? WallAnchor.End : WallAnchor.Start,
        })

      setTimeout(() => createWallGaps(), 100)
    }

    this.scene?.onAfterRenderObservable.addOnce(() => {
      this.enableShadows()
      this.selectionManager.updatePickingList()
    })
  }

  enableItemsSelection() {
    this.scene?.meshes.forEach((m) => {
      if (isSelectableMesh(m)) {
        this.addItemSelection(m)
      }
    })
  }

  changeBuildingMode(mode: BuildingViewMode, selectedItemId: string | null) {
    switch (mode) {
      case BuildingViewMode.SingleStorey:
        this.buildingViewMode = new SingleStoreyMode()
        break
      case BuildingViewMode.StackableView:
        this.buildingViewMode = new StackableViewMode()
        break
      case BuildingViewMode.WholeBuilding:
        this.buildingViewMode = new WholeBuildingMode()
        break
      default:
        this.buildingViewMode = new WholeBuildingMode()
        break
    }

    this.buildingViewMode.activate(selectedItemId)
    this.updateGridPosition()
  }

  updateCameraTargetScreenOffset(x?: number, y?: number) {
    if (!this.camera) return

    this.camera.targetScreenOffset.x = x ?? 0
    this.camera.targetScreenOffset.y = y ?? 0
  }

  toggleStructuralElements(types: BuildingStructuralTypes[]) {
    const activeTypes = getEnums(BuildingStructuralTypes)
      .filter(([_, type]) => types.includes(type))
      .map(([_, value]) => value)

    const selectedBuildingMode =
      store.getState().construction.selectedBuildingMode
    const selectedStoreyId = store.getState().construction.selectedStoreyId
    const animationComplete =
      store.getState().structural.structuralAnimationStatus ===
      StructuralAnimationStatus.Completed

    this.scene?.meshes.forEach((mesh) => {
      if (
        isStructuralMesh(mesh) &&
        (selectedBuildingMode === BuildingViewMode.WholeBuilding ||
          selectedBuildingMode === BuildingViewMode.StackableView ||
          (selectedBuildingMode === BuildingViewMode.SingleStorey &&
            selectedStoreyId === mesh.parent?.id) || // Structural wall's parent is storey mesh
          (selectedBuildingMode === BuildingViewMode.SingleStorey &&
            selectedStoreyId === mesh.parent?.parent?.id) || // Stud's parent is wall mesh, and wall's parent is storey mesh
          isFoundationMesh(mesh))
      ) {
        mesh.isVisible = activeTypes.includes(mesh.metadata.structuralType)
        mesh.isPickable =
          activeTypes.includes(mesh.metadata.structuralType) &&
          animationComplete
      }
    })

    this.scene?.meshes
      .filter((mesh) => !isStructuralMesh(mesh) && isSelectableMesh(mesh))
      .forEach((mesh) => {
        mesh.isPickable = !!!types.length
      })

    this.selectionManager.deselect()
  }

  toggleStructuralElementsPicking(pickingEnabled: boolean) {
    const selectedBuildingMode =
      store.getState().construction.selectedBuildingMode
    const selectedStoreyId = store.getState().construction.selectedStoreyId

    this.scene?.meshes.forEach((mesh) => {
      if (
        isStructuralMesh(mesh) &&
        (selectedBuildingMode === BuildingViewMode.WholeBuilding ||
          selectedBuildingMode === BuildingViewMode.StackableView ||
          (selectedBuildingMode === BuildingViewMode.SingleStorey &&
            selectedStoreyId === mesh.parent?.id) || // Structural wall's parent is storey mesh
          (selectedBuildingMode === BuildingViewMode.SingleStorey &&
            selectedStoreyId === mesh.parent?.parent?.id)) // Stud's parent is wall mesh, and wall's parent is storey mesh
      ) {
        mesh.isPickable = pickingEnabled
      }
    })
  }

  toggleBuildingElementsPicking(
    elementTypes: BuildingElementType[],
    pickingEnabled: boolean,
  ) {
    this.scene?.meshes
      .filter(
        (mesh) =>
          !isStructuralMesh(mesh) &&
          isSelectableMesh(mesh) &&
          elementTypes.includes(mesh.metadata.type),
      )
      .forEach((mesh) => {
        mesh.isPickable = pickingEnabled
      })
  }

  setWallsNormalsAccordingToBuildingCenter() {
    this.scene?.meshes
      .filter((mesh) => isStoreyMesh(mesh))
      .forEach((storeyMesh) => {
        const boundingVectors = storeyMesh.getHierarchyBoundingVectors()
        const storeyPosition = Vector3.TransformCoordinates(
          Vector3.Lerp(boundingVectors.min, boundingVectors.max, 0.5),
          storeyMesh.getWorldMatrix(),
        )

        const wallMeshes =
          storeyMesh.getChildMeshes(
            false,
            (mesh) => mesh instanceof AbstractMesh && isWallMesh(mesh),
          ) ?? []

        for (const wallMesh of wallMeshes) {
          const bbox = wallMesh.getBoundingInfo().boundingBox
          const center = Vector3.TransformNormal(
            bbox.centerWorld,
            wallMesh.getWorldMatrix(),
          )

          const vectorToPoint = storeyPosition.subtract(center).normalize()
          const dotProduct = Vector3.Dot(
            wallMesh.metadata.wallNormal,
            vectorToPoint,
          )
          if (dotProduct < 0) {
            wallMesh.metadata.wallNormal.negateInPlace()
          }
        }
      })
  }

  createGrid() {
    let gridMesh = this.scene?.getMeshByName('Grid')
    if (gridMesh) {
      return
    }

    const gridMaterial = this.materialManager.getGridMaterial()
    gridMesh = MeshBuilder.CreateGround(
      'Grid',
      { width: 100000, height: 100000 },
      this.scene,
    )
    gridMesh.material = gridMaterial
    this.updateGridPosition()
  }

  private updateGridPosition() {
    const gridMesh = this.scene?.getMeshByName('Grid')
    if (!gridMesh) {
      return
    }

    const selectedBuildingMode =
      store.getState().construction.selectedBuildingMode
    const selectedStoreyId = store.getState().construction.selectedStoreyId

    const wallMeshes =
      this.scene?.meshes.reduce<AbstractMesh[]>((acc, current) => {
        if (isStoreyMesh(current)) {
          if (
            selectedBuildingMode === BuildingViewMode.WholeBuilding ||
            selectedBuildingMode === BuildingViewMode.StackableView ||
            (selectedBuildingMode === BuildingViewMode.SingleStorey &&
              current.id === selectedStoreyId)
          ) {
            const wallMeshes = current.getChildMeshes(
              true,
              (mesh) => mesh instanceof AbstractMesh && isWallMesh(mesh),
            )

            acc.push(...wallMeshes)
          }
        }

        return acc
      }, []) ?? []

    if (wallMeshes.length > 0) {
      const boundingBox = computeBoundingBox(wallMeshes)
      gridMesh.position.y = boundingBox.min.y
    }
  }

  private updateElementStatus(payload: {
    elementType: BuildingElementType
    elementSubtype?: BuildingElementSubType
    status: BuildingElementStatus
  }) {
    store.dispatch<any>(updateBuildingElementStatus(payload))
  }

  private updateWallTopFaceColor(mesh: AbstractMesh, newColor: string) {
    const vertexData = mesh.getVerticesData(VertexBuffer.ColorKind)
    if (!vertexData) {
      return
    }

    const color4 = Color4.FromColor3(Color3.FromHexString(newColor), 1)
    for (let i = 0; i < vertexData.length; i += 4) {
      if (newColor !== this.topFaceColor.toHexString()) {
        if (
          vertexData[i] === this.topFaceColor.r &&
          vertexData[i + 1] === this.topFaceColor.g &&
          vertexData[i + 2] === this.topFaceColor.b
        ) {
          vertexData[i] = color4.r
          vertexData[i + 1] = color4.g
          vertexData[i + 2] = color4.b
          vertexData[i + 3] = color4.a
        }
      } else {
        if (
          !(
            vertexData[i] === this.topFaceColor.r &&
            vertexData[i + 1] === this.topFaceColor.g &&
            vertexData[i + 2] === this.topFaceColor.b
          ) &&
          !(
            vertexData[i] === this.otherFacesColor.r &&
            vertexData[i + 1] === this.otherFacesColor.g &&
            vertexData[i + 2] === this.otherFacesColor.b
          )
        ) {
          vertexData[i] = color4.r
          vertexData[i + 1] = color4.g
          vertexData[i + 2] = color4.b
          vertexData[i + 3] = color4.a
        }
      }
    }

    mesh.setVerticesData(VertexBuffer.ColorKind, vertexData, true)
  }

  private drawGround(polygon: number[][]) {
    const contour: Vector3[] = []
    if (polygon.length) {
      for (let i = 0; i < polygon.length; i++) {
        const point = new Vector3(polygon[i][0], 0, polygon[i][1])
        contour.push(point)
      }
    } else {
      const convexHull = this.getConvexHull(this.groundContour)
      for (const point of convexHull) {
        contour.push(new Vector3(point.x, 0, point.y))
      }
    }

    if (contour.length) {
      const groundDepth = 5
      const ground = MeshBuilder.ExtrudePolygon(
        'Flooring',
        {
          shape: contour,
          depth: groundDepth,
          faceUV: [
            new Vector4(0, 0, 1, 1),
            new Vector4(0, 0, 1, 1),
            new Vector4(0, 0, 1, 1),
          ],
        },
        this.scene,
        earcut,
      )
      ground.position.y = groundDepth
      ground.bakeCurrentTransformIntoVertices()
      ground.metadata = {
        type: BuildingElementType.Floor,
      }

      return ground
    }

    return new Mesh('Flooring')
  }

  private calculateOuterContour(innerContour: Vector2[], thickness: number[]) {
    const outerContour = []

    for (let i = 0; i < innerContour.length; i++) {
      const p1 = innerContour[i]
      const p2 = innerContour[(i + 1) % innerContour.length]

      // Calculate the direction vector
      const direction = p2.subtract(p1).normalize()

      // Calculate the perpendicular vector
      const perpendicular = new Vector2(-direction.y, direction.x)

      // Offset points
      const offsetP1 = p1.add(perpendicular.scale(thickness[i]))
      const offsetP2 = p2.add(perpendicular.scale(thickness[i]))

      outerContour.push({ offsetP1, offsetP2 })
    }

    // Calculate intersections for the offset segments and store in correct order
    const finalOuterContour = []
    for (let i = 0; i < innerContour.length; i++) {
      const segment1 = outerContour[i]
      const segment2 = outerContour[(i + 1) % innerContour.length]

      const intersection = this.calculateIntersection(
        segment1.offsetP1,
        segment1.offsetP2,
        segment2.offsetP1,
        segment2.offsetP2,
      )
      if (intersection) {
        finalOuterContour.push(intersection)
      }
    }

    // Ensure the outer contour points are in the correct order
    const orderedOuterContour = []
    for (let i = 0; i < innerContour.length; i++) {
      const prevIndex = (i - 1 + innerContour.length) % innerContour.length
      orderedOuterContour.push(finalOuterContour[prevIndex])
    }

    return orderedOuterContour
  }

  // Function to calculate intersection of two lines
  private calculateIntersection(
    p1: Vector2,
    p2: Vector2,
    p3: Vector2,
    p4: Vector2,
  ) {
    const A1 = p2.y - p1.y
    const B1 = p1.x - p2.x
    const C1 = A1 * p1.x + B1 * p1.y

    const A2 = p4.y - p3.y
    const B2 = p3.x - p4.x
    const C2 = A2 * p3.x + B2 * p3.y

    const det = A1 * B2 - A2 * B1

    if (det === 0) {
      return null // Lines are parallel
    } else {
      const x = (B2 * C1 - B1 * C2) / det
      const y = (A1 * C2 - A2 * C1) / det
      return new Vector2(x, y)
    }
  }

  private addItemSelection(mesh: AbstractMesh) {
    let rootMesh: AbstractMesh
    let meshes: AbstractMesh[] = []
    if (!mesh.geometry) {
      rootMesh = mesh
      meshes = rootMesh.getChildMeshes().filter((m) => m.geometry)
    } else {
      meshes.push(mesh)
    }

    for (const mesh of meshes) {
      if (!mesh.actionManager) {
        mesh.actionManager = new ActionManager(this.scene)
      }

      const actionExists = mesh.actionManager.actions.some((action) => {
        return (
          action.trigger === ActionManager.OnPickTrigger &&
          action instanceof ExecuteCodeAction
        )
      })

      if (!actionExists) {
        mesh.actionManager.registerAction(
          new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
            const selectedTool = store.getState().construction.selectedTool
            const selectedConstructionTab =
              store.getState().construction.selectedConstructionTab
            const isEditorTab =
              selectedConstructionTab === ConstructionTab.Editor
            const isValidationTab =
              selectedConstructionTab === ConstructionTab.Validation
            const codeValidationDiscipline: CodeValidationDiscipline =
              mesh.metadata?.codeValidationDiscipline

            if (
              !isSelectableMesh(rootMesh ? rootMesh : mesh) ||
              (!isEditorTab &&
                (!isValidationTab || !codeValidationDiscipline)) ||
              isSelectionIgnored(selectedTool)
            ) {
              return
            }

            if (
              this.selectionManager.isSelected(rootMesh ? rootMesh.id : mesh.id)
            ) {
              if (this.selectionManager.selectedCount > 1) {
                this.selectionManager.select({
                  elementsIds: rootMesh ? [rootMesh.id] : [mesh.id],
                })
              } else {
                this.selectionManager.deselect()
              }
            } else {
              this.selectionManager.select({
                elementsIds: rootMesh ? [rootMesh.id] : [mesh.id],
              })
            }

            if (isValidationTab) {
              const selectedCodeValidationRule =
                store.getState().validation.selectedCodeValidationRule

              const isSelected =
                !!selectedCodeValidationRule?.elementsIds?.includes(
                  rootMesh ? rootMesh.id : mesh.id,
                )
              if (isSelected) {
                store.dispatch(deselectCodeValidationRule())
              } else {
                const codeValidationRules = store
                  .getState()
                  .validation.codeValidationRules[
                    codeValidationDiscipline
                  ].reduce<CodeValidationRule[]>((arr, current) => {
                    if (
                      !current.elementsIds?.includes(
                        rootMesh ? rootMesh.id : mesh.id,
                      )
                    ) {
                      return arr
                    }

                    if (current.status === CodeValidationStatus.Failed) {
                      return [current, ...arr]
                    } else {
                      return [...arr, current]
                    }
                  }, [])

                if (codeValidationRules[0]) {
                  store.dispatch(
                    selectCodeValidationRule({ item: codeValidationRules[0] }),
                  )
                }
              }
            }
          }),
        )
      }
    }
  }

  private updateCameraPosition() {
    if (this.camera) {
      const buildingItems =
        this.scene?.meshes.filter((mesh) => isStoreyMesh(mesh)) ?? []
      const buildingBBox = getBuildingBoundingBox()
      if (buildingBBox) {
        const cameraTarget = Vector3.Lerp(
          buildingBBox.min,
          buildingBBox.max,
          0.5,
        )
        const maxHeight = buildingBBox.max.y - buildingBBox.min.y

        this.camera.setTarget(cameraTarget, false, false, false)
        this.camera.setPosition(
          new Vector3(
            this.camera.position.x,
            buildingBBox.min.y +
              (buildingItems[buildingItems.length - 1]?.position.y ?? 0) +
              maxHeight,
            this.camera.position.z,
          ),
        )
        this.camera.lowerRadiusLimit = this.camera.radius / 4
        this.camera.upperRadiusLimit = this.camera.radius * 4
      }
    }
  }

  enableEdgeRendering() {
    this.scene?.meshes
      .filter((mesh) => isSketchMesh(mesh))
      .forEach((mesh) => {
        mesh.isVisible = mesh.parent
          ? (mesh.parent as AbstractMesh).isVisible
          : true
      })
    /*this.scene?.registerBeforeRender(() => {
      this.scene?.meshes
        .filter((m) => m.isVisible && isWallMesh(m))
        .forEach((wallMesh) => {
          const camera = this.camera
          if (!camera) {
            return
          }

          const dashRatio = 0.5
          const upperRadiusLimit = camera.upperRadiusLimit!
          const lowerRadiusLimit = camera.lowerRadiusLimit!

          const maxDashLength = 10
          const minDashLength = 1
          const dashLength = mapValue(
            camera.radius,
            { min: lowerRadiusLimit, max: upperRadiusLimit },
            { min: minDashLength, max: maxDashLength },
          )

          const maxEdgeWidth = 2
          const minEdgeWidth = 0.5
          const edgeWidth = mapValue(
            camera.radius,
            { min: lowerRadiusLimit, max: upperRadiusLimit },
            { min: minEdgeWidth, max: maxEdgeWidth },
          )

          const wallMeshBoundingBox = wallMesh.getBoundingInfo().boundingBox
          const isWallMeshTransparent =
            wallMesh.material ===
            this.materialManager.getInactiveMaterial(BuildingElementType.Wall)
          const wallEdgeMeshes = wallMesh.getChildMeshes<GreasedLineMesh>(
            false,
            (node): node is GreasedLineMesh =>
              node instanceof GreasedLineMesh && node.name === 'WallEdge',
          )

          if (wallEdgeMeshes.length === 0) {
            if (!wallMesh.edgesRenderer) {
              wallMesh.enableEdgesRendering(0.95, true, {
                applyTessellation: true,
                useFastVertexMerger: false,
              })
              wallMesh.edgesWidth = 0
            }

            const linePositions = wallMesh.edgesRenderer?.linesPositions
            if (!linePositions) return

            for (let i = 0; i < linePositions.length; i += 3 * 4) {
              const x1 = linePositions[i]
              const y1 = linePositions[i + 1]
              const z1 = linePositions[i + 2]

              const x2 = linePositions[i + 6]
              const y2 = linePositions[i + 6 + 1]
              const z2 = linePositions[i + 6 + 2]

              const edgeLength = Math.sqrt(
                (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2,
              )
              const dashCount = Math.round(edgeLength / dashLength)
              const isTopFaceEdge =
                !compare(y1, wallMeshBoundingBox.maximumWorld.y) &&
                !compare(y2, wallMeshBoundingBox.maximumWorld.y)
              const wallEdgeMesh = CreateGreasedLine(
                'WallEdge',
                {
                  points: [x1, y1, z1, x2, y2, z2],
                },
                {
                  useDash: isWallMeshTransparent,
                  dashCount,
                  dashRatio,
                  color:
                    isTopFaceEdge && !isWallMeshTransparent
                      ? this.topFaceColor
                      : this.edgesColor,
                  width: edgeWidth,
                },
                wallMesh.getScene(),
              )

              wallEdgeMesh.parent = wallMesh
              wallEdgeMesh.isPickable = false
            }
          } else {
            wallEdgeMeshes.forEach((wallEdgeMesh) => {
              const [x1, y1, z1, x2, y2, z2] = wallEdgeMesh.points[0]

              if (wallEdgeMesh.greasedLineMaterial) {
                wallEdgeMesh.greasedLineMaterial.width = edgeWidth
                wallEdgeMesh.greasedLineMaterial.useDash = isWallMeshTransparent
                if (wallEdgeMesh.greasedLineMaterial.useDash) {
                  const edgeLength = Math.sqrt(
                    (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2,
                  )
                  const dashCount = Math.round(edgeLength / dashLength)
                  wallEdgeMesh.greasedLineMaterial.dashCount = dashCount
                  wallEdgeMesh.greasedLineMaterial.color = this.edgesColor
                } else {
                  const wallEdgeMeshAbsoluteY = wallEdgeMesh.absolutePosition.y
                  const isTopFaceEdge =
                    !compare(
                      y1 + wallEdgeMeshAbsoluteY,
                      wallMeshBoundingBox.maximumWorld.y,
                    ) &&
                    !compare(
                      y2 + wallEdgeMeshAbsoluteY,
                      wallMeshBoundingBox.maximumWorld.y,
                    )
                  wallEdgeMesh.greasedLineMaterial.color = isTopFaceEdge
                    ? this.topFaceColor
                    : this.edgesColor
                }
              }
            })
          }
        })
    })*/
  }

  private elementOnPickDownTriggerMap: Map<
    (mesh: AbstractMesh) => boolean,
    ExecuteCodeAction
  > = new Map()
  private changeCursorObserver: Observer<PointerInfo> | null = null

  enableCreateWallPickDownTrigger() {
    if (!this.scene) {
      return
    }

    this.selectionManager.deselect()
    this.disableElementPickDownTrigger()

    let isDragging = false
    const scene = this.scene
    const canvas = this.scene.getEngine().getRenderingCanvas()
    this.changeCursorObserver = this.scene.onPointerObservable.add(
      (pointerInfo) => {
        switch (pointerInfo.type) {
          case PointerEventTypes.POINTERMOVE:
            const pickResult = scene.pick(scene.pointerX, scene.pointerY)
            if (canvas) {
              if (isDragging) {
                canvas.style.cursor = dragCursor
              } else {
                if (pickResult.hit && isWallMesh(pickResult.pickedMesh!)) {
                  canvas.style.cursor = createCursor
                } else {
                  canvas.style.cursor = 'default'
                }
              }
            }
            break
        }
      },
    )

    const gridSettings = store.getState().construction.gridSettings
    const createWallPickDownTrigger = new ExecuteCodeAction(
      ActionManager.OnPickDownTrigger,
      (event) => {
        const wallMesh = event.meshUnderPointer
        if (!wallMesh || !this.scene || !this.camera) {
          return
        }

        const ray = this.scene.createPickingRay(
          event.pointerX,
          event.pointerY,
          null,
          this.camera,
        )

        const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
        const height = wallBoundingBox.extendSize.y * 2
        const intersection = rayBoundingBoxIntersection(ray, wallBoundingBox)
        if (!intersection) {
          return
        }

        // New Wall Direction
        const wallDirection = intersection.pickNormal
        // New Wall Normal
        const wallNormal = Vector3.Cross(wallDirection, Vector3.UpReadOnly)

        // Ignore if face normal direction equal to UP Vector
        if (Math.abs(wallDirection.dot(Vector3.UpReadOnly)) > 1e-3) {
          return
        }

        const pointerDragBehavior = new PointerDragBehavior({
          dragPlaneNormal: Vector3.UpReadOnly,
        })
        pointerDragBehavior.useObjectOrientationForDragging = false
        pointerDragBehavior.moveAttached = false

        // Create new Wall
        const thickness =
          store.getState().construction.selectedToolProps.wallThickness
        const tempWallMesh = MeshBuilder.CreateBox(
          'TempWall',
          {
            height,
            depth: thickness,
          },
          this.scene,
        )
        tempWallMesh.setPivotPoint(new Vector3(-0.5, 0, 0))
        Vector3.RotationFromAxisToRef(
          wallDirection,
          Vector3.UpReadOnly,
          wallNormal,
          tempWallMesh.rotation,
        )
        tempWallMesh.position.set(
          intersection.pickPoint.x + 0.5,
          wallMesh.position.y,
          intersection.pickPoint.z,
        )
        tempWallMesh.scaling.x = 0
        tempWallMesh.material = this.materialManager.getNormalMaterial(
          BuildingElementType.Wall,
        )

        const gridMaterial = this.materialManager.getGridMaterial()
        const gridSize = gridMaterial ? gridMaterial.gridRatio : 1
        // Extend and move new Wall
        pointerDragBehavior.onDragObservable.add((event) => {
          isDragging = true
          const dot = Vector3.Dot(
            wallDirection,
            event.dragPlanePoint.subtractToRef(
              intersection.pickPoint,
              TmpVectors.Vector3[0],
            ),
          )

          const snappedLength = Math.max(
            Math.round(dot / gridSize) * gridSize,
            gridSize,
          )

          const position = wallDirection
            .scaleToRef(-dot, TmpVectors.Vector3[0])
            .addInPlace(event.dragPlanePoint)

          const snappedX = Math.round(position.x / gridSize) * gridSize
          const snappedZ = Math.round(position.z / gridSize) * gridSize

          tempWallMesh.scaling.x = gridSettings.snapToGrid
            ? snappedLength
            : Math.max(dot, 0)
          tempWallMesh.position.x = gridSettings.snapToGrid
            ? snappedX
            : position.x
          tempWallMesh.position.z = gridSettings.snapToGrid
            ? snappedZ
            : position.z
        })

        // Save new Wall
        pointerDragBehavior.onDragEndObservable.add(() => {
          isDragging = false
          wallMesh.removeBehavior(pointerDragBehavior)

          const length = tempWallMesh.scaling.x
          if (length < 0.01) {
            tempWallMesh.dispose()
            return
          }

          const newWallPosition = tempWallMesh
            .getBoundingInfo()
            .boundingBox.centerWorld.clone()
          const center_point = [newWallPosition.x, newWallPosition.z]
          const angle = rad2Deg(tempWallMesh.rotation.y)
          const bottom_elevation = newWallPosition.y - height / 2
          tempWallMesh.dispose()

          const wall: CreateWallOptions = {
            id: uuid(),
            type: store.getState().construction.selectedToolProps.wallType,
            length,
            height,
            thickness,
            center_point,
            bottom_elevation,
            angle,
          }

          const newWallMesh = this.createWall(wall, wallMesh.parent as Mesh)
          this.addItemSelection(newWallMesh)
          newWallMesh.actionManager?.registerAction(createWallPickDownTrigger)
          this.enableShadows()
          this.drawMeshSketch(newWallMesh, 0, 0)

          this.sceneManager
            .getEventEmitter()
            ?.emit(EventType.BuildingAddWallElement, {
              ...wall,
              storeyId: wallMesh.parent?.id,
            })

          setTimeout(() => createWallGaps(), 100)
        })

        wallMesh.addBehavior(pointerDragBehavior)
      },
    )
    this.elementOnPickDownTriggerMap.set(isWallMesh, createWallPickDownTrigger)

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  enableCreateWindowPickDownTrigger() {
    if (!this.scene) {
      return
    }

    this.selectionManager.deselect()

    this.disableElementPickDownTrigger()

    const localMatrix = new Matrix()
    const planeIntersectionPoint = new Vector3()
    const wallDimension = new Vector3()
    const center = new Vector3()
    const scaledDirection = new Vector3()

    const gridSettings = store.getState().construction.gridSettings
    const createWindowPickDownTrigger = new ExecuteCodeAction(
      ActionManager.OnPickDownTrigger,
      (event) => {
        const wallOrGapMesh = event.meshUnderPointer
        const wallDirection = wallOrGapMesh?.metadata?.wallDirection
        const wallNormal = wallOrGapMesh?.metadata?.wallNormal
        if (
          !wallOrGapMesh ||
          !wallDirection ||
          !wallNormal ||
          !this.scene ||
          !this.camera
        ) {
          return
        }

        const bbox = wallOrGapMesh.getBoundingInfo().boundingBox
        bbox.extendSize.scaleToRef(2, wallDimension)
        wallOrGapMesh.getWorldMatrix().invertToRef(localMatrix)
        const centerX = bbox.center.x

        const sizeType =
          store.getState().construction.selectedToolProps.windowSize
        const windowWidth = BuildingWindowSizes[sizeType].width
        const windowHeight = BuildingWindowSizes[sizeType].height
        const windowThickness = wallDimension.z
        if (wallDimension.x < windowWidth || wallDimension.y < windowHeight) {
          return
        }

        const pointerDragBehavior = new PointerDragBehavior({
          dragPlaneNormal: wallNormal,
        })
        pointerDragBehavior.useObjectOrientationForDragging = false
        pointerDragBehavior.moveAttached = false

        const plane = new Plane(0, 0, 1, 0)
        const ray = this.scene.createPickingRay(
          this.scene.pointerX,
          this.scene.pointerY,
          wallOrGapMesh.getWorldMatrix(),
          this.camera,
        )

        const distance = ray.intersectsPlane(plane)
        if (!distance) {
          return
        }

        const windowMesh = MeshBuilder.CreateBox(
          'Window',
          {
            width: windowWidth,
            height: windowHeight,
            depth: windowThickness,
          },
          this.scene,
        )
        windowMesh.id = uuid()
        windowMesh.scaling.z += 1 / windowThickness
        windowMesh.rotation.copyFrom(wallOrGapMesh.rotation)
        windowMesh.material = this.materialManager.getHighlightMaterial(
          BuildingElementType.Window,
        )
        windowMesh.metadata = {
          isHidden: false,
          subType: wallOrGapMesh.metadata.subtype,
          isTransparent: true,
          type: BuildingElementType.Window,
          hasSketch: true,
        }

        const gridMaterial = this.materialManager.getGridMaterial()
        const gridSize = gridMaterial ? gridMaterial.gridRatio : 1
        const updateWindowMeshPosition = (
          distance: number,
          fixed?: boolean,
        ) => {
          // Calculate ray-plane intersection point
          planeIntersectionPoint
            .copyFrom(ray.direction)
            .scaleInPlace(distance)
            .addInPlace(ray.origin)

          planeIntersectionPoint.x = fixed
            ? centerX
            : Math.min(
                Math.abs(planeIntersectionPoint.x),
                (wallDimension.x + windowWidth) / 2,
              ) * Math.sign(planeIntersectionPoint.x)
          planeIntersectionPoint.y =
            Math.min(
              Math.abs(planeIntersectionPoint.y),
              (wallDimension.y + windowHeight) / 2,
            ) * Math.sign(planeIntersectionPoint.y)

          if (gridSettings.snapToGrid) {
            const snappedX =
              Math.round(planeIntersectionPoint.x / gridSize) * gridSize
            const snappedY =
              Math.round(planeIntersectionPoint.y / gridSize) * gridSize

            planeIntersectionPoint.x = snappedX
            planeIntersectionPoint.y = snappedY
          }

          Vector3.TransformCoordinatesToRef(
            planeIntersectionPoint,
            wallOrGapMesh.getWorldMatrix(),
            windowMesh.position,
          )
        }

        updateWindowMeshPosition(distance, isWallGapMesh(wallOrGapMesh))

        pointerDragBehavior.onDragObservable.add(() => {
          if (!this.scene || !this.camera) {
            return
          }

          this.scene.createPickingRayToRef(
            this.scene.pointerX,
            this.scene.pointerY,
            wallOrGapMesh.getWorldMatrix(),
            ray,
            this.camera,
          )

          const distance = ray.intersectsPlane(plane)
          if (!distance) {
            return
          }

          updateWindowMeshPosition(distance, isWallGapMesh(wallOrGapMesh))
        })

        pointerDragBehavior.onDragEndObservable.add(() => {
          wallOrGapMesh.removeBehavior(pointerDragBehavior)

          windowMesh.scaling.setAll(1.0)
          windowMesh.material = this.materialManager.getInactiveMaterial(
            BuildingElementType.Window,
          )

          let wallMesh = wallOrGapMesh
          let isBatchOperation = false
          if (isWallGapMesh(wallOrGapMesh)) {
            let maxLength = Number.MIN_VALUE
            for (const wallId of wallOrGapMesh.metadata.wallIds) {
              const mesh = this.scene?.getMeshById(wallId)
              if (mesh && isWallMesh(mesh)) {
                const length = mesh.getBoundingInfo().boundingBox.extendSize.x
                if (length > maxLength) {
                  maxLength = length
                  wallMesh = mesh
                }
              }
            }

            if (wallMesh) {
              const wallId = wallMesh?.id
              const wallMeshParent = wallMesh?.parent
              const wallSubType = wallMesh?.metadata.subType
              const wallMaterial = wallMesh?.material
              const wallBoundingBox = wallMesh?.getBoundingInfo().boundingBox
              const wallOrGapMeshBoundingBox =
                wallOrGapMesh.getBoundingInfo().boundingBox
              const height = wallBoundingBox.extendSize.y * 2
              const thickness = wallBoundingBox.extendSize.z * 2
              const bottom_elevation = wallMesh.position.y - height / 2
              const angle = rad2Deg(wallMesh.rotation.y)
              const minBbox = Vector3.Minimize(
                wallBoundingBox.minimumWorld,
                wallOrGapMeshBoundingBox.minimumWorld,
              )
              const maxBbox = Vector3.Maximize(
                wallBoundingBox.maximumWorld,
                wallOrGapMeshBoundingBox.maximumWorld,
              )
              let newCenterPoint = Vector3.Center(minBbox, maxBbox)
              let newLength =
                wallBoundingBox.maximum.x -
                wallBoundingBox.minimum.x +
                (wallOrGapMeshBoundingBox.maximum.x -
                  wallOrGapMeshBoundingBox.minimum.x)
              const wallStructuralMeshes = wallMesh
                ?.getChildMeshes(
                  true,
                  (mesh) =>
                    mesh instanceof AbstractMesh && isStructuralMesh(mesh),
                )
                .map((mesh) => mesh.setParent(null))
              const wallOpeningsMeshes = wallMesh?.getChildMeshes(
                true,
                (mesh) =>
                  mesh instanceof AbstractMesh &&
                  (isWindowMesh(mesh) || isDoorMesh(mesh)),
              )

              wallOpeningsMeshes.forEach((mesh) => mesh.setParent(null))
              wallMesh.dispose()

              const wall: CreateWallOptions = {
                id: wallId,
                type: wallSubType,
                length: newLength,
                height,
                thickness,
                bottom_elevation,
                center_point: [newCenterPoint.x, newCenterPoint.z],
                angle,
              }

              let newWallMesh = this.createWall(wall, wallMeshParent as Mesh)
              const newWallMeshMetadata = newWallMesh.metadata
              const newWallMeshCSG = CSG.FromMesh(newWallMesh)
              newWallMesh.dispose()

              wallOpeningsMeshes?.forEach((child) => {
                const childCSG = CSG.FromMesh(child as Mesh)
                newWallMeshCSG.subtractInPlace(childCSG)
              })

              newWallMesh = newWallMeshCSG.toMesh(
                'Wall',
                wallMaterial,
                this.scene,
              )
              newWallMesh.id = wall.id
              newWallMesh.setParent(wallMeshParent!)
              wallOpeningsMeshes?.forEach((m) => m.setParent(newWallMesh))
              wallStructuralMeshes?.forEach((m) => m.setParent(newWallMesh))
              newWallMesh.metadata = newWallMeshMetadata

              this.addItemSelection(newWallMesh)
              this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
                if (filterFun(newWallMesh)) {
                  newWallMesh.actionManager?.registerAction(action)
                }
              })
              this.enableShadows()
              this.drawMeshSketch(newWallMesh, 0, 0)

              this.sceneManager
                .getEventEmitter()
                ?.emit(EventType.BuildingEditWallElement, {
                  id: wall.id,
                  length: wall.length,
                  center_point: wall.center_point,
                  anchor: WallAnchor.Start,
                })

              wallMesh = newWallMesh
              isBatchOperation = true
            }
          }

          const wallId = wallMesh.id
          const wallMeshParent = wallMesh.parent
          const wallMeshChildren = wallMesh
            .getChildMeshes(true)
            .map((mesh) => mesh.setParent(null))
          const wallMeshMetadata = wallMesh.metadata
          const wallMaterial = wallMesh.material

          const windowCSG = CSG.FromMesh(windowMesh)
          const wallCSG = CSG.FromMesh(wallMesh as Mesh)
          wallMesh.dispose(true)
          wallCSG.subtractInPlace(windowCSG)

          const updatedWallMesh = wallCSG.toMesh(
            'Wall',
            wallMaterial,
            this.scene,
          )
          updatedWallMesh.id = wallId
          updatedWallMesh.setParent(wallMeshParent)
          windowMesh.setParent(updatedWallMesh)
          wallMeshChildren.forEach((m) => m.setParent(updatedWallMesh))
          updatedWallMesh.computeWorldMatrix(true)
          updatedWallMesh.metadata = wallMeshMetadata

          this.addItemSelection(windowMesh)
          this.drawMeshSketch(windowMesh, 0, 0)

          this.addItemSelection(updatedWallMesh)
          updatedWallMesh.actionManager?.registerAction(
            createWindowPickDownTrigger,
          )
          this.enableShadows()

          const wallPosition = updatedWallMesh.getAbsolutePosition()
          const windowPosition = windowMesh.getAbsolutePosition()
          const sillHeight =
            windowPosition.y -
            (wallPosition.y - wallDimension.y / 2) -
            windowHeight / 2

          wallPosition.addToRef(
            wallDirection.scaleToRef(-wallDimension.x / 2, scaledDirection),
            center,
          )
          const centerLocationOnWall = Math.sqrt(
            (windowPosition.x - center.x) ** 2 +
              (windowPosition.z - center.z) ** 2,
          )

          const windowDirection = windowPosition.subtractToRef(
            center,
            scaledDirection,
          )
          scaledDirection.y = 0
          scaledDirection.normalize()
          const isNegative = isAntiParallel(wallDirection, windowDirection)

          const createWindowPayload: CreateWindowPayload = {
            id: windowMesh.id,
            wall_id: updatedWallMesh.id,
            type: updatedWallMesh.metadata.subtype,
            width: windowWidth,
            height: windowHeight,
            thickness: windowThickness,
            center_location_on_wall: isNegative
              ? -centerLocationOnWall
              : centerLocationOnWall,
            sill_height: sillHeight,
          }

          this.sceneManager
            .getEventEmitter()
            ?.emit(EventType.BuildingAddWindowElement, {
              window: createWindowPayload,
              isBatchOperation,
            })

          setTimeout(() => createWallGaps(), 100)
        })

        wallOrGapMesh.addBehavior(pointerDragBehavior)
      },
    )
    this.elementOnPickDownTriggerMap.set(
      isWallOrWallGap,
      createWindowPickDownTrigger,
    )

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  enableCreateDoorPickDownTrigger() {
    if (!this.scene) {
      return
    }

    this.selectionManager.deselect()

    this.disableElementPickDownTrigger()

    const localMatrix = new Matrix()
    const planeIntersectionPoint = new Vector3()
    const wallDimension = new Vector3()
    const center = new Vector3()
    const scaledDirection = new Vector3()

    const gridSettings = store.getState().construction.gridSettings
    const createDoorPickDownTrigger = new ExecuteCodeAction(
      ActionManager.OnPickDownTrigger,
      (event) => {
        const wallOrGapMesh = event.meshUnderPointer
        const wallDirection = wallOrGapMesh?.metadata?.wallDirection
        const wallNormal = wallOrGapMesh?.metadata?.wallNormal
        if (
          !wallOrGapMesh ||
          !wallDirection ||
          !wallNormal ||
          !this.scene ||
          !this.camera
        ) {
          return
        }

        const bbox = wallOrGapMesh.getBoundingInfo().boundingBox
        bbox.extendSize.scaleToRef(2, wallDimension)
        wallOrGapMesh.getWorldMatrix().invertToRef(localMatrix)
        const centerX = bbox.center.x

        const sizeType =
          store.getState().construction.selectedToolProps.doorSize
        const doorWidth = BuildingDoorSizes[sizeType].width
        const doorHeight = BuildingDoorSizes[sizeType].height
        const doorThickness = wallDimension.z
        if (wallDimension.x < doorWidth || wallDimension.y < doorHeight) {
          return
        }

        const pointerDragBehavior = new PointerDragBehavior({
          dragPlaneNormal: wallNormal,
        })
        pointerDragBehavior.useObjectOrientationForDragging = false
        pointerDragBehavior.moveAttached = false

        const plane = new Plane(0, 0, 1, 0)
        const ray = this.scene.createPickingRay(
          this.scene.pointerX,
          this.scene.pointerY,
          wallOrGapMesh.getWorldMatrix(),
          this.camera,
        )

        const distance = ray.intersectsPlane(plane)
        if (!distance) {
          return
        }

        const doorMesh = MeshBuilder.CreateBox(
          'Door',
          {
            width: doorWidth,
            height: doorHeight,
            depth: doorThickness,
          },
          this.scene,
        )
        doorMesh.id = uuid()
        doorMesh.scaling.z += 1 / doorThickness
        doorMesh.rotation.copyFrom(wallOrGapMesh.rotation)
        doorMesh.material = this.materialManager.getHighlightMaterial(
          BuildingElementType.Door,
        )
        doorMesh.metadata = {
          isHidden: false,
          subType: wallOrGapMesh.metadata.subtype,
          isTransparent: true,
          type: BuildingElementType.Door,
          hasSketch: true,
        }

        const gridMaterial = this.materialManager.getGridMaterial()
        const gridSize = gridMaterial ? gridMaterial.gridRatio : 1
        const updateDoorMeshPosition = (distance: number, fixed?: boolean) => {
          // Calculate ray-plane intersection point
          planeIntersectionPoint
            .copyFrom(ray.direction)
            .scaleInPlace(distance)
            .addInPlace(ray.origin)

          planeIntersectionPoint.x = fixed
            ? centerX
            : Math.min(
                Math.abs(planeIntersectionPoint.x),
                (wallDimension.x + doorWidth) / 2,
              ) * Math.sign(planeIntersectionPoint.x)
          planeIntersectionPoint.y = (doorHeight - wallDimension.y) / 2

          if (gridSettings.snapToGrid && !fixed) {
            const snappedX =
              Math.round(planeIntersectionPoint.x / gridSize) * gridSize

            planeIntersectionPoint.x = snappedX
          }

          Vector3.TransformCoordinatesToRef(
            planeIntersectionPoint,
            wallOrGapMesh.getWorldMatrix(),
            doorMesh.position,
          )
        }

        updateDoorMeshPosition(distance, isWallGapMesh(wallOrGapMesh))

        pointerDragBehavior.onDragObservable.add(() => {
          if (!this.scene || !this.camera) {
            return
          }

          this.scene.createPickingRayToRef(
            this.scene.pointerX,
            this.scene.pointerY,
            wallOrGapMesh.getWorldMatrix(),
            ray,
            this.camera,
          )

          const distance = ray.intersectsPlane(plane)
          if (!distance) {
            return
          }

          updateDoorMeshPosition(distance, isWallGapMesh(wallOrGapMesh))
        })

        pointerDragBehavior.onDragEndObservable.add(() => {
          wallOrGapMesh.removeBehavior(pointerDragBehavior)

          doorMesh.scaling.setAll(1.0)
          doorMesh.material = this.materialManager.getInactiveMaterial(
            BuildingElementType.Door,
          )

          let wallMesh = wallOrGapMesh
          let isBatchOperation = false
          if (isWallGapMesh(wallOrGapMesh)) {
            let maxLength = Number.MIN_VALUE
            for (const wallId of wallOrGapMesh.metadata.wallIds) {
              const mesh = this.scene?.getMeshById(wallId)
              if (mesh && isWallMesh(mesh)) {
                const length = mesh.getBoundingInfo().boundingBox.extendSize.x
                if (length > maxLength) {
                  maxLength = length
                  wallMesh = mesh
                }
              }
            }

            if (wallMesh) {
              const wallId = wallMesh?.id
              const wallMeshParent = wallMesh?.parent
              const wallSubType = wallMesh?.metadata.subType
              const wallMaterial = wallMesh?.material
              const wallBoundingBox = wallMesh?.getBoundingInfo().boundingBox
              const wallOrGapMeshBoundingBox =
                wallOrGapMesh.getBoundingInfo().boundingBox
              const height = wallBoundingBox.extendSize.y * 2
              const thickness = wallBoundingBox.extendSize.z * 2
              const bottom_elevation = wallMesh.position.y - height / 2
              const angle = rad2Deg(wallMesh.rotation.y)
              const minBbox = Vector3.Minimize(
                wallBoundingBox.minimumWorld,
                wallOrGapMeshBoundingBox.minimumWorld,
              )
              const maxBbox = Vector3.Maximize(
                wallBoundingBox.maximumWorld,
                wallOrGapMeshBoundingBox.maximumWorld,
              )
              let newCenterPoint = Vector3.Center(minBbox, maxBbox)
              let newLength =
                wallBoundingBox.maximum.x -
                wallBoundingBox.minimum.x +
                (wallOrGapMeshBoundingBox.maximum.x -
                  wallOrGapMeshBoundingBox.minimum.x)
              const wallStructuralMeshes = wallMesh
                ?.getChildMeshes(
                  true,
                  (mesh) =>
                    mesh instanceof AbstractMesh && isStructuralMesh(mesh),
                )
                .map((mesh) => mesh.setParent(null))
              const wallOpeningsMeshes = wallMesh?.getChildMeshes(
                true,
                (mesh) =>
                  mesh instanceof AbstractMesh &&
                  (isWindowMesh(mesh) || isDoorMesh(mesh)),
              )

              wallOpeningsMeshes.forEach((mesh) => mesh.setParent(null))
              wallMesh.dispose()

              const wall: CreateWallOptions = {
                id: wallId,
                type: wallSubType,
                length: newLength,
                height,
                thickness,
                bottom_elevation,
                center_point: [newCenterPoint.x, newCenterPoint.z],
                angle,
              }

              let newWallMesh = this.createWall(wall, wallMeshParent as Mesh)
              const newWallMeshMetadata = newWallMesh.metadata
              const newWallMeshCSG = CSG.FromMesh(newWallMesh)
              newWallMesh.dispose()

              wallOpeningsMeshes?.forEach((child) => {
                const childCSG = CSG.FromMesh(child as Mesh)
                newWallMeshCSG.subtractInPlace(childCSG)
              })

              newWallMesh = newWallMeshCSG.toMesh(
                'Wall',
                wallMaterial,
                this.scene,
              )
              newWallMesh.id = wall.id
              newWallMesh.setParent(wallMeshParent!)
              wallOpeningsMeshes?.forEach((m) => m.setParent(newWallMesh))
              wallStructuralMeshes?.forEach((m) => m.setParent(newWallMesh))
              newWallMesh.metadata = newWallMeshMetadata

              this.addItemSelection(newWallMesh)
              this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
                if (filterFun(newWallMesh)) {
                  newWallMesh.actionManager?.registerAction(action)
                }
              })
              this.enableShadows()
              this.drawMeshSketch(newWallMesh, 0, 0)

              this.sceneManager
                .getEventEmitter()
                ?.emit(EventType.BuildingEditWallElement, {
                  id: wall.id,
                  length: wall.length,
                  center_point: wall.center_point,
                  anchor: WallAnchor.Start,
                })

              wallMesh = newWallMesh
              isBatchOperation = true
            }
          }

          const wallId = wallMesh.id
          const wallMeshParent = wallMesh.parent
          const wallMeshChildren = wallMesh
            .getChildMeshes(true)
            .map((mesh) => mesh.setParent(null))
          const wallMeshMetadata = wallMesh.metadata
          const wallMaterial = wallMesh.material

          const doorCSG = CSG.FromMesh(doorMesh)
          const wallCSG = CSG.FromMesh(wallMesh as Mesh)
          wallMesh.dispose(true)
          wallCSG.subtractInPlace(doorCSG)

          const updatedWallMesh = wallCSG.toMesh(
            'Wall',
            wallMaterial,
            this.scene,
          )
          updatedWallMesh.id = wallId
          updatedWallMesh.setParent(wallMeshParent)
          doorMesh.setParent(updatedWallMesh)
          wallMeshChildren.forEach((m) => m.setParent(updatedWallMesh))
          updatedWallMesh.computeWorldMatrix(true)
          updatedWallMesh.metadata = wallMeshMetadata

          this.addItemSelection(doorMesh)
          this.drawMeshSketch(doorMesh, 0, 0)

          this.addItemSelection(updatedWallMesh)
          updatedWallMesh.actionManager?.registerAction(
            createDoorPickDownTrigger,
          )
          this.enableShadows()

          const wallPosition = wallMesh.getAbsolutePosition()
          const doorPosition = doorMesh.getAbsolutePosition()

          wallPosition.addToRef(
            wallDirection.scaleToRef(-wallDimension.x / 2, scaledDirection),
            center,
          )
          const centerLocationOnWall = Math.sqrt(
            (doorPosition.x - center.x) ** 2 + (doorPosition.z - center.z) ** 2,
          )

          const doorDirection = doorPosition.subtractToRef(
            center,
            scaledDirection,
          )
          scaledDirection.y = 0
          scaledDirection.normalize()
          const isNegative = isAntiParallel(wallDirection, doorDirection)

          const createDoorPayload: CreateDoorPayload = {
            id: doorMesh.id,
            wall_id: updatedWallMesh.id,
            type: updatedWallMesh.metadata.subtype,
            width: doorWidth,
            height: doorHeight,
            thickness: doorThickness,
            center_location_on_wall: isNegative
              ? -centerLocationOnWall
              : centerLocationOnWall,
            sill_height: 0,
          }

          this.sceneManager
            .getEventEmitter()
            ?.emit(EventType.BuildingAddDoorElement, {
              door: createDoorPayload,
              isBatchOperation,
            })

          setTimeout(() => createWallGaps(), 100)
        })

        wallOrGapMesh.addBehavior(pointerDragBehavior)
      },
    )
    this.elementOnPickDownTriggerMap.set(
      isWallOrWallGap,
      createDoorPickDownTrigger,
    )

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  enableCreateBeamPickDownTrigger() {
    if (!this.scene) {
      return
    }

    this.selectionManager.deselect()

    this.disableElementPickDownTrigger()

    const gridSettings = store.getState().construction.gridSettings
    const createBeamPickDownTrigger = new ExecuteCodeAction(
      ActionManager.OnPickDownTrigger,
      (event) => {
        const wallMesh = event.meshUnderPointer
        if (!wallMesh || !this.scene || !this.camera) {
          return
        }

        const ray = this.scene.createPickingRay(
          event.pointerX,
          event.pointerY,
          null,
          this.camera,
        )

        const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
        const intersection = rayBoundingBoxIntersection(ray, wallBoundingBox)
        if (!intersection) {
          return
        }

        // New Beam Direction
        const beamDirection = intersection.pickNormal
        // New Beam Normal
        const beamNormal = Vector3.Cross(beamDirection, Vector3.UpReadOnly)

        // Ignore if face normal direction equal to UP Vector
        if (Math.abs(beamDirection.dot(Vector3.UpReadOnly)) > 1e-3) {
          return
        }

        const pointerDragBehavior = new PointerDragBehavior({
          dragPlaneNormal: Vector3.UpReadOnly,
        })
        pointerDragBehavior.useObjectOrientationForDragging = false
        pointerDragBehavior.moveAttached = false
        pointerDragBehavior.updateDragPlane = false

        const sizeType =
          store.getState().construction.selectedToolProps.beamSize
        const thickness = BuildingBeamSizes[sizeType].thickness
        const width = BuildingBeamSizes[sizeType].width

        // Create new Beam
        const tempBeamMesh = MeshBuilder.CreateBox(
          'TempBeam',
          {
            height: width,
            depth: thickness,
          },
          this.scene,
        )
        tempBeamMesh.isPickable = false
        tempBeamMesh.setPivotPoint(new Vector3(-0.5, 0, 0))
        Vector3.RotationFromAxisToRef(
          beamDirection,
          Vector3.UpReadOnly,
          beamNormal,
          tempBeamMesh.rotation,
        )
        tempBeamMesh.position.set(
          intersection.pickPoint.x + 0.5,
          wallBoundingBox.maximumWorld.y - width / 2,
          intersection.pickPoint.z,
        )
        tempBeamMesh.scaling.x = 0
        tempBeamMesh.material = this.materialManager.getStructuralMaterial(
          BuildingStructuralTypes.Beam,
        )

        const gridMaterial = this.materialManager.getGridMaterial()
        const gridSize = gridMaterial ? gridMaterial.gridRatio : 1
        pointerDragBehavior.onDragObservable.add((event) => {
          const dot = Vector3.Dot(
            beamDirection,
            event.dragPlanePoint.subtractToRef(
              intersection.pickPoint,
              TmpVectors.Vector3[0],
            ),
          )

          const snappedLength = Math.max(
            Math.round(dot / gridSize) * gridSize,
            gridSize,
          )

          const position = beamDirection
            .scaleToRef(
              gridSettings.snapToGrid ? -snappedLength : -dot,
              TmpVectors.Vector3[0],
            )
            .addInPlace(event.dragPlanePoint)

          tempBeamMesh.scaling.x = gridSettings.snapToGrid
            ? snappedLength
            : Math.max(dot, 0)

          // Snap position to grid
          const snappedX = Math.round(position.x / gridSize) * gridSize
          const snappedZ = Math.round(position.z / gridSize) * gridSize

          const minX = wallBoundingBox.minimumWorld.x + thickness / 2
          const maxX = wallBoundingBox.maximumWorld.x - thickness / 2
          const minZ = wallBoundingBox.minimumWorld.z + thickness / 2
          const maxZ = wallBoundingBox.maximumWorld.z - thickness / 2

          tempBeamMesh.position.x = Math.min(
            Math.max(gridSettings.snapToGrid ? snappedX : position.x, minX),
            maxX,
          )
          tempBeamMesh.position.z = Math.min(
            Math.max(gridSettings.snapToGrid ? snappedZ : position.z, minZ),
            maxZ,
          )
        })

        wallMesh.addBehavior(pointerDragBehavior)

        pointerDragBehavior.onDragEndObservable.add((event) => {
          // Save new Beam
          wallMesh.removeBehavior(pointerDragBehavior)

          const length = tempBeamMesh.scaling.x
          if (length < 0.01) {
            tempBeamMesh.dispose()
            return
          }

          const newBeamPosition = tempBeamMesh
            .getBoundingInfo()
            .boundingBox.centerWorld.clone()
          const boundingVectors = (
            wallMesh.parent as Mesh
          ).getHierarchyBoundingVectors(
            true,
            (m: AbstractMesh) => isWallMesh(m) || isGroundMesh(m),
          )
          const elevation = boundingVectors.min.y
          const center_point = {
            x: newBeamPosition.x,
            y: newBeamPosition.z,
            z: newBeamPosition.y - elevation,
          }
          tempBeamMesh.dispose()

          const axisX = new Vector3(1, 0, 0)
          const dotProduct = Vector3.Dot(axisX, beamDirection)
          const angle = Math.acos(
            dotProduct / (axisX.length() * beamDirection.length()),
          )
          const beam: RectangleFramingElement = {
            id: uuid(),
            length,
            width,
            thickness,
            center_point,
            rotation_x: 0,
            rotation_y: 0,
            rotation_z: Tools.ToDegrees(angle),
          }

          this.createRectangleFramingElement(
            [beam],
            BuildingStructuralTypes.Beam,
            RectangleFramingElementType.Beam,
            wallMesh.parent as Mesh,
          )
          const newBeamMesh = this.scene?.getMeshById(beam.id)
          if (newBeamMesh) {
            this.addItemSelection(newBeamMesh)
            this.enableShadows()

            this.sceneManager
              .getEventEmitter()
              ?.emit(EventType.BuildingAddStructuralElement, {
                element: beam,
                elementType: RectangleFramingElementType.Beam,
                storeyId: wallMesh.parent?.id,
              })
          } else {
            console.warn('Error during beam creation')
          }
        })
      },
    )
    this.elementOnPickDownTriggerMap.set(isWallMesh, createBeamPickDownTrigger)

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  enableEditBeamOnPickDownTrigger() {
    if (!this.scene) {
      return
    }

    this.selectionManager.deselect()

    this.disableElementPickDownTrigger()

    const gridSettings = store.getState().construction.gridSettings
    const editBeamPickDownTrigger = new ExecuteCodeAction(
      ActionManager.OnPickDownTrigger,
      (event) => {
        const isSeparateWallUIVisible = SeparateElementUI.isUIVisible
        const beamMesh = event.meshUnderPointer as Mesh
        const beam = store
          .getState()
          .construction.convertData.building.storeys.flatMap(
            (storey) => storey.beams,
          )
          .find((beam) => beam.id === beamMesh.id)
        if (
          !beamMesh ||
          !isBeamMesh(beamMesh) ||
          !beam ||
          !this.scene ||
          !this.camera ||
          isSeparateWallUIVisible
        ) {
          return
        }

        const ray = this.scene.createPickingRay(
          this.scene.pointerX,
          this.scene.pointerY,
          null,
          this.camera,
        )
        const beamBoundingBox = beamMesh.getBoundingInfo().boundingBox
        const intersection = rayBoundingBoxIntersection(ray, beamBoundingBox)

        const rotationX = Matrix.RotationX(Tools.ToRadians(beam.rotation_x))
        const rotationY = Matrix.RotationY(Tools.ToRadians(beam.rotation_z))
        const rotationZ = Matrix.RotationZ(Tools.ToRadians(beam.rotation_y))
        // Combine rotation matrices
        const transformationMatrix = rotationX
          .multiply(rotationY)
          .multiply(rotationZ)
        const beamDirection = Vector3.TransformNormal(
          Axis.X,
          transformationMatrix,
        )
        if (!intersection || Math.abs(intersection.pickNormal.y) > 1e-3) {
          return
        }

        const directionRay = new Ray(beamMesh.position, beamDirection)
        const pickInfo = this.scene.pickWithRay(
          directionRay,
          (mesh) => isWallMesh(mesh),
          true,
        )
        const wallBoundingBox =
          pickInfo && pickInfo.hit
            ? pickInfo.pickedMesh?.getBoundingInfo().boundingBox
            : null
        const boundingBoxMinX = wallBoundingBox
          ? wallBoundingBox.minimumWorld.x
          : Number.MIN_VALUE
        const boundingBoxMaxX = wallBoundingBox
          ? wallBoundingBox.maximumWorld.x
          : Number.MAX_VALUE
        const boundingBoxMinZ = wallBoundingBox
          ? wallBoundingBox.minimumWorld.z
          : Number.MIN_VALUE
        const boundingBoxMaxZ = wallBoundingBox
          ? wallBoundingBox.maximumWorld.z
          : Number.MAX_VALUE

        const directionRay2 = new Ray(beamMesh.position, beamDirection.negate())
        const pickInfo2 = this.scene.pickWithRay(
          directionRay2,
          (mesh) => isWallMesh(mesh),
          true,
        )
        const wallBoundingBox2 =
          pickInfo2 && pickInfo2.hit
            ? pickInfo2.pickedMesh?.getBoundingInfo().boundingBox
            : null
        const boundingBoxMinX2 = wallBoundingBox2
          ? wallBoundingBox2.minimumWorld.x
          : Number.MIN_VALUE
        const boundingBoxMaxX2 = wallBoundingBox2
          ? wallBoundingBox2.maximumWorld.x
          : Number.MAX_VALUE
        const boundingBoxMinZ2 = wallBoundingBox2
          ? wallBoundingBox2.minimumWorld.z
          : Number.MIN_VALUE
        const boundingBoxMaxZ2 = wallBoundingBox2
          ? wallBoundingBox2.maximumWorld.z
          : Number.MAX_VALUE

        const minX = Math.max(
          boundingBoxMinX + beam.thickness / 2,
          boundingBoxMinX2 + beam.thickness / 2,
        )
        const maxX = Math.min(
          boundingBoxMaxX + beam.thickness / 2,
          boundingBoxMaxX2 + beam.thickness / 2,
        )
        const minZ = Math.max(
          boundingBoxMinZ + beam.thickness / 2,
          boundingBoxMinZ2 + beam.thickness / 2,
        )
        const maxZ = Math.min(
          boundingBoxMaxZ + beam.thickness / 2,
          boundingBoxMaxZ2 + beam.thickness / 2,
        )

        const crossProduct = Vector3.Cross(
          beamDirection,
          intersection.pickNormal,
        )

        const localPoint = Vector3.TransformCoordinates(
          event?.additionalData.pickedPoint,
          beamMesh.getWorldMatrix().clone().invert(),
        )
        const dimensions = beamBoundingBox.extendSize.scale(2)
        const halfLength = dimensions.x / 2
        const center75 = halfLength * 0.75
        const coord = localPoint.x
        const isInCenter = Math.abs(coord) <= center75

        const gridMaterial = this.materialManager.getGridMaterial()
        const gridSize = gridMaterial ? gridMaterial.gridRatio : 1
        let pointerDragBehavior: PointerDragBehavior

        const localPickedPoint = Vector3.TransformCoordinates(
          event?.additionalData.pickedPoint,
          beamMesh.getWorldMatrix().clone().invert(),
        )
        const localVector = localPickedPoint.subtract(Vector3.Zero())
        const isTowardPositiveX = localVector.x >= 0
        const adjustedBeamDirection = isTowardPositiveX
          ? beamDirection
          : beamDirection.negate()
        if (
          (Math.abs(crossProduct.x) < 1e-3 &&
            Math.abs(crossProduct.y) < 1e-3 &&
            Math.abs(crossProduct.z) < 1e-3) ||
          !isInCenter
        ) {
          pointerDragBehavior = new PointerDragBehavior({
            dragAxis: beamDirection,
          })
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false
          pointerDragBehavior.onDragObservable.add((event) => {
            const halfLength = beamMesh.scaling.x / 2
            const fixedPoint = beamMesh.position.subtract(
              adjustedBeamDirection.scale(halfLength),
            )
            const dragVector = event.dragPlanePoint.subtract(fixedPoint)
            const projectedLength = Vector3.Dot(
              dragVector,
              adjustedBeamDirection,
            )
            const snappedLength = gridSettings.snapToGrid
              ? Math.round(projectedLength / gridSize) * gridSize
              : projectedLength

            if (snappedLength > 0.1) {
              beamMesh.scaling.x = snappedLength
              beamMesh.position = fixedPoint.add(
                adjustedBeamDirection.scale(snappedLength / 2),
              )
            }
          })
        } else {
          pointerDragBehavior = new PointerDragBehavior({
            dragAxis: intersection.pickNormal,
          })
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false
          pointerDragBehavior.onDragObservable.add((event) => {
            // Move along the normal
            beamMesh.position.addInPlace(
              intersection.pickNormal.scale(event.dragDistance / 10),
            )

            if (gridSettings.snapToGrid) {
              // Snap to grid
              beamMesh.position.x =
                Math.round(beamMesh.position.x / gridSize) * gridSize
              beamMesh.position.z =
                Math.round(beamMesh.position.z / gridSize) * gridSize
            }

            // Clamp to bounding box limits
            if (Math.abs(intersection.pickNormal.x) > 1e-3) {
              beamMesh.position.x = Math.min(
                Math.max(beamMesh.position.x, minX),
                maxX,
              )
            }
            if (Math.abs(intersection.pickNormal.z) > 1e-3) {
              beamMesh.position.z = Math.min(
                Math.max(beamMesh.position.z, minZ),
                maxZ,
              )
            }
          })
        }

        if (pointerDragBehavior) {
          beamMesh.addBehavior(pointerDragBehavior)
          pointerDragBehavior.onDragEndObservable.add(() => {
            beamMesh.removeBehavior(pointerDragBehavior)

            const boundingVectors = (
              beamMesh.parent as Mesh
            ).getHierarchyBoundingVectors(
              true,
              (m: AbstractMesh) => isWallMesh(m) || isGroundMesh(m),
            )
            const elevation = boundingVectors.min.y
            const center_point = {
              x: beamMesh.position.x,
              y: beamMesh.position.z,
              z: beamMesh.position.y - elevation,
            }
            const length = Math.abs(beamMesh.scaling.x)
            const updatedBeam = { ...beam, length, center_point }
            this.sceneManager
              .getEventEmitter()
              ?.emit(EventType.BuildingEditStructuralElement, {
                id: updatedBeam.id,
                length: updatedBeam.length,
                center_point: updatedBeam.center_point,
                elementType: RectangleFramingElementType.Beam,
              })
            const parent = beamMesh.parent
            beamMesh.dispose()
            this.createRectangleFramingElement(
              [updatedBeam],
              BuildingStructuralTypes.Beam,
              RectangleFramingElementType.Beam,
              parent as Mesh,
            )

            const newBeamMesh = this.scene?.meshes.find(
              (mesh) => mesh.id === updatedBeam.id && !mesh.isDisposed(),
            )
            if (newBeamMesh) {
              this.addItemSelection(newBeamMesh)
              newBeamMesh.actionManager?.registerAction(editBeamPickDownTrigger)
            }
          })
        }
      },
    )
    this.elementOnPickDownTriggerMap.set(isBeamMesh, editBeamPickDownTrigger)

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  disableElementPickDownTrigger() {
    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = this.scene?.meshes.filter(filterFun) ?? []
      for (const target of targetMeshes) {
        target.actionManager?.unregisterAction(action)
      }
    })

    this.elementOnPickDownTriggerMap.clear()
    if (this.changeCursorObserver) {
      this.scene?.onPointerObservable.remove(this.changeCursorObserver)
    }
  }

  addPickTriggers(itemIds: string[]) {
    if (!this.elementOnPickDownTriggerMap.size) {
      return
    }

    const meshes = itemIds.reduce<AbstractMesh[]>((acc, itemId) => {
      const mesh = this.scene?.getMeshById(itemId)
      if (!mesh) {
        return acc
      }

      acc.push(mesh, ...mesh.getChildMeshes())

      return acc
    }, [])

    for (const mesh of meshes) {
      if (isSelectableMesh(mesh)) {
        this.addItemSelection(mesh)
      }
    }

    this.elementOnPickDownTriggerMap.forEach((action, filterFun) => {
      const targetMeshes = meshes.filter(filterFun)
      for (const target of targetMeshes) {
        if (!target.actionManager) {
          target.actionManager = new ActionManager(this.scene)
        }

        target.actionManager.registerAction(action)
      }
    })
  }

  enableMarqueeSelection() {
    this.selectionManager.deselect()
    this.sceneManager.setCameraLock(true)
    this.selectionManager.enableMarqueeSelection()
  }

  disableMarqueeSelection() {
    this.selectionManager.deselect()
    this.selectionManager.disableMarqueeSelection()
    this.sceneManager.setCameraLock(false)
  }

  async replaceBuildingElements(
    elementsIds: string[],
    bimObject: BimObject,
    updateHole?: boolean,
  ) {
    for (const elementId of elementsIds) {
      const elementToReplace = this.scene?.meshes.find(
        (m) => m.id === elementId,
      )
      if (!elementToReplace) {
        console.error('Error during replacing building element')
        return
      }

      let min
      let max
      let targetCenter
      if (isBimObjectMesh(elementToReplace)) {
        const targetBoundingBox = computeBoundingBox(
          elementToReplace.getChildMeshes(),
          true,
        )
        const scalingMatrix = Matrix.Scaling(
          elementToReplace.scaling.x,
          elementToReplace.scaling.y,
          elementToReplace.scaling.z,
        )
        min = Vector3.TransformCoordinates(targetBoundingBox.min, scalingMatrix)
        max = Vector3.TransformCoordinates(targetBoundingBox.max, scalingMatrix)
        const boundingVectors = elementToReplace.getHierarchyBoundingVectors()
        targetCenter = Vector3.Center(boundingVectors.min, boundingVectors.max)
      } else {
        const targetBoundingBox = elementToReplace.getBoundingInfo().boundingBox
        min = targetBoundingBox.minimum
        max = targetBoundingBox.maximum
        targetCenter = targetBoundingBox.centerWorld
      }
      const scaling = new Vector3(1, 1, 1)
      const position = Vector3.Zero()
      const rotation = new Quaternion()
      elementToReplace.getWorldMatrix().decompose(scaling, rotation, position)

      let bimElement = this.scene?.getMeshById(bimObject.id)
      if (!bimElement) {
        const bimManager = BimManager.getInstance()
        let bimElementSource = bimManager.getScene()?.getMeshById(bimObject.id)
        if (!bimElementSource) {
          await BimManager.getInstance().showBimObject(bimObject)
          bimElementSource = bimManager.getScene()?.getMeshById(bimObject.id)
          if (!bimElementSource) {
            console.error('Can not load BIM object')
            return
          }
        }

        const serializedBimSource = serializeMeshWithMaterials(bimElementSource)
        const meshes = await importMeshWithMaterials(
          serializedBimSource,
          this.scene!,
        )
        bimElement = meshes[0]
        bimElement.isVisible = false
        bimElement.setEnabled(true)
        bimElement.getChildMeshes().forEach((m) => (m.isVisible = false))
      }

      const instance = bimElement?.instantiateHierarchy() as AbstractMesh
      if (!instance || !bimElement) return
      const id = elementToReplace.id
      const type = isBimObjectMesh(elementToReplace)
        ? elementToReplace.metadata.sourceType
        : elementToReplace.metadata.type
      let boundingVectors = bimElement.getHierarchyBoundingVectors()
      if (!bimObject.isNonEditable) {
        const scalingX =
          (max.x - min.x) / (boundingVectors.max.x - boundingVectors.min.x)
        const scalingY =
          (max.y - min.y) / (boundingVectors.max.y - boundingVectors.min.y)
        const scalingZ =
          (max.z - min.z) / (boundingVectors.max.z - boundingVectors.min.z)
        instance.scaling.set(scalingX, scalingY, scalingZ)
      }

      instance.rotationQuaternion = rotation.clone()
      const size = boundingVectors.max.subtract(boundingVectors.min)
      const center = boundingVectors.min.add(size.scale(0.5))
      const offset = center.multiply(instance.scaling)
      let matrix: Matrix = Matrix.Identity()
      Matrix.FromQuaternionToRef(rotation, matrix)
      const rotatedOffset = Vector3.TransformCoordinates(offset, matrix)
      instance.position = targetCenter.subtract(rotatedOffset)

      const wallMeshId = elementToReplace.parent?.id
      if (updateHole) {
        this.removeBuildingElement(elementToReplace)
      } else {
        elementToReplace.id = ''
        elementToReplace.setParent(null)
        this.scene?.removeMesh(elementToReplace, true)
        if (isBimObjectMesh(elementToReplace)) {
          elementToReplace
            .getChildMeshes()
            .forEach((m) => m.actionManager?.dispose())
          elementToReplace.dispose()
        } else {
          elementToReplace.actionManager?.dispose()
          elementToReplace.dispose()
        }
      }

      const metadata = { sourceType: type }
      instance.isVisible = true
      instance.id = id
      instance.metadata = {
        type: BuildingElementType.BimObject,
        sourceType: type,
        bimObjectId: bimObject.id,
        isHidden: false,
        hasSketch: true,
      }
      instance.getChildren(undefined, false).forEach((m) => {
        m.id = uuid()
        ;(m as Mesh).isVisible = true
        m.metadata = metadata
      })
      const wallMesh = this.scene?.meshes.find(
        (m) => m.id === wallMeshId && !m.isDisposed(),
      ) as Mesh
      if (wallMesh && isWallMesh(wallMesh)) {
        if (updateHole) {
          cutOpeningHolesInWallMesh(wallMesh, [instance as Mesh])
        }
        this.addItemSelection(instance)
        const childrenWithGeometry = instance
          .getChildMeshes()
          .filter((m) => m.geometry && !isSketchMesh(m))
        for (const child of childrenWithGeometry) {
          this.drawMeshSketch(child, 0, 0)
        }
        instance.setParent(wallMesh)
        if (this.selectionManager.isSelected(instance.id)) {
          this.selectionManager.select({
            elementsIds: [instance.id],
            silent: true,
          })
        }
      }
    }
  }

  private createRectangleFramingElement(
    elements: RectangleFramingElement[],
    type: BuildingStructuralTypes,
    elementType: RectangleFramingElementType,
    parentMesh: Mesh,
  ) {
    const rectangleFramingElementMesh =
      this.rectangleFramingElementMeshes.get(type)
    if (!rectangleFramingElementMesh) {
      console.warn(`Base RectangleFramingElementMesh for ${type} was not found`)
      return
    }

    for (const element of elements) {
      const existingRectangleFramingElement = this.scene?.getMeshById(
        element.id,
      )
      if (
        existingRectangleFramingElement &&
        !existingRectangleFramingElement.isDisposed()
      ) {
        continue
      }

      const rectangleFramingElementInstance =
        rectangleFramingElementMesh.createInstance(
          `${elementType}RectangleFramingElementInstance`,
        )

      rectangleFramingElementInstance.scaling.set(
        element.length,
        element.width,
        element.thickness,
      )

      rectangleFramingElementInstance.rotation.set(
        deg2Rad(element.rotation_x),
        deg2Rad(element.rotation_z),
        deg2Rad(element.rotation_y),
      )

      let elevation = 0
      if (
        isWallMesh(parentMesh) ||
        isDoorMesh(parentMesh) ||
        isWindowMesh(parentMesh) ||
        isRoofMesh(parentMesh) ||
        isRidgeMesh(parentMesh) ||
        isBeamMesh(parentMesh) ||
        isHeaderMesh(parentMesh)
      ) {
        const boundingVectors = parentMesh.getHierarchyBoundingVectors(false)
        elevation = boundingVectors.min.y
      } else if (isStoreyMesh(parentMesh)) {
        const boundingVectors = parentMesh.getHierarchyBoundingVectors(
          true,
          (m) => isWallMesh(m) || isGroundMesh(m),
        )
        elevation = boundingVectors.min.y
      } else if (isSpanMesh(parentMesh)) {
        const storeyMesh = parentMesh.parent ?? parentMesh
        if (!parentMesh.parent) {
          console.warn(
            `${type} structural mesh parent (span mesh) has no parent (storey mesh)`,
          )
        }

        const boundingVectors = storeyMesh.getHierarchyBoundingVectors(
          true,
          (m) => isWallMesh(m) || isGroundMesh(m),
        )
        elevation = boundingVectors.min.y
      } else if (isFoundationMesh(parentMesh)) {
        elevation = parentMesh.position.y
      }

      rectangleFramingElementInstance.position.set(
        element.center_point.x,
        elevation + element.center_point.z,
        element.center_point.y,
      )
      rectangleFramingElementInstance.setParent(parentMesh)

      const isEngineeringPageActive =
        store.getState().construction.selectedConstructionPage ===
        ConstructionPage.Engineering
      const animationComplete =
        store.getState().structural.structuralAnimationStatus ===
        StructuralAnimationStatus.Completed
      rectangleFramingElementInstance.id = element.id
      rectangleFramingElementInstance.isVisible =
        isEngineeringPageActive && animationComplete
      rectangleFramingElementInstance.isPickable =
        isEngineeringPageActive && animationComplete

      rectangleFramingElementInstance.instancedBuffers.color =
        elementType === RectangleFramingElementType.Post
          ? Color3.Black()
          : Color3.FromHexString(StructuralBackgroundColor[type])

      rectangleFramingElementInstance.metadata = {
        type: BuildingElementType.Structural,
        structuralType: type,
        rectangleFramingElementType: elementType,
      }

      if (
        [
          BuildingStructuralTypes.Beam,
          BuildingStructuralTypes.Joist,
          BuildingStructuralTypes.Header,
        ].includes(type)
      ) {
        if (!rectangleFramingElementInstance.edgesRenderer) {
          rectangleFramingElementInstance.enableEdgesRendering(0.9999)
        }

        const color = Color4.FromHexString('#1D1C1F')
        rectangleFramingElementInstance.edgesColor = color
        rectangleFramingElementInstance.edgesWidth = 40
        rectangleFramingElementInstance.metadata.edgesColor = color
      }
    }
  }

  private createFoundation(foundation: Foundation) {
    const existingFoundation = this.scene?.getMeshById(foundation.id) as Mesh
    if (existingFoundation && !existingFoundation.isDisposed()) {
      return existingFoundation
    }

    const foundationMesh = new Mesh('Foundation', this.scene)
    foundationMesh.id = foundation.id
    foundationMesh.position.y = foundation.top_elevation
    foundationMesh.metadata = {
      type: BuildingElementType.Structural,
      structuralType: BuildingStructuralTypes.Foundation,
    }

    // Create foundation beams
    this.createRectangleFramingElement(
      foundation.beams,
      BuildingStructuralTypes.Foundation,
      RectangleFramingElementType.Beam,
      foundationMesh,
    )

    for (const beam of foundation.beams) {
      const beamMesh = this.scene?.getMeshById(beam.id) as Mesh
      if (!beamMesh) {
        console.warn(`Beam mesh with id ${beam.id} not found`)
        continue
      }

      if (beam.posts) {
        this.createRectangleFramingElement(
          beam.posts,
          BuildingStructuralTypes.Foundation,
          RectangleFramingElementType.Post,
          beamMesh,
        )
      }
    }

    // Create foundation pile caps and piles
    this.createPileCaps(foundation.pile_caps, foundationMesh)

    return foundationMesh
  }

  private createPileCaps(pileCaps: PileCap[], foundationMesh: Mesh) {
    const pileCapMesh = this.rectangleFramingElementMeshes.get(
      BuildingStructuralTypes.Foundation,
    )
    if (!pileCapMesh) {
      console.warn(
        `Base RectangleFramingElementMesh for Foundation was not found`,
      )
      return
    }

    for (const pileCap of pileCaps) {
      const existingPileCap = this.scene?.getMeshById(pileCap.id)
      if (existingPileCap && !existingPileCap.isDisposed()) {
        continue
      }

      const pileCapInstance = pileCapMesh.createInstance('PileCapInstance')

      pileCapInstance.scaling.set(
        pileCap.length_x,
        pileCap.length_z,
        pileCap.length_y,
      )

      const topElevation = foundationMesh.position.y
      pileCapInstance.position.set(
        pileCap.top_center_point.x,
        topElevation + pileCap.top_center_point.z - pileCap.length_z / 2, // because it is top point, not center point
        pileCap.top_center_point.y,
      )
      pileCapInstance.setParent(foundationMesh)

      const isEngineeringPageActive =
        store.getState().construction.selectedConstructionPage ===
        ConstructionPage.Engineering
      const animationComplete =
        store.getState().structural.structuralAnimationStatus ===
        StructuralAnimationStatus.Completed

      pileCapInstance.id = pileCap.id
      pileCapInstance.isVisible = isEngineeringPageActive && animationComplete
      pileCapInstance.isPickable = isEngineeringPageActive && animationComplete

      pileCapInstance.metadata = {
        type: BuildingElementType.Structural,
        structuralType: BuildingStructuralTypes.Foundation,
      }

      this.createPiles(pileCap.piles, pileCapInstance, topElevation)
    }
  }

  private createPiles(
    piles: Pile[],
    pileCapMesh: InstancedMesh,
    topElevation: number,
  ) {
    for (const pile of piles) {
      const existingPile = this.scene?.getMeshById(pile.id)
      if (existingPile && !existingPile.isDisposed()) {
        continue
      }

      const pileInstance = this.pileElementMesh.createInstance('PileInstance')
      pileInstance.scaling.set(pile.diameter, pile.length, pile.diameter)
      pileInstance.position.set(
        pile.top_center_point.x,
        topElevation + pile.top_center_point.z - pile.length / 2, // because it is top point, not center point
        pile.top_center_point.y,
      )
      pileInstance.setParent(pileCapMesh)

      const isEngineeringPageActive =
        store.getState().construction.selectedConstructionPage ===
        ConstructionPage.Engineering
      const animationComplete =
        store.getState().structural.structuralAnimationStatus ===
        StructuralAnimationStatus.Completed

      pileInstance.id = pile.id
      pileInstance.isVisible = isEngineeringPageActive && animationComplete
      pileInstance.isPickable = isEngineeringPageActive && animationComplete
      pileInstance.instancedBuffers.color = Color3.FromHexString(
        StructuralBackgroundColor[BuildingStructuralTypes.Foundation],
      )

      pileInstance.metadata = {
        type: BuildingElementType.Structural,
        structuralType: BuildingStructuralTypes.Foundation,
      }
    }
  }

  private getConvexHull(points: Vector2[]): Vector2[] {
    points.sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x))

    function cross(o: Vector2, a: Vector2, b: Vector2): number {
      return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
    }

    const lower: Vector2[] = []
    for (let i = 0; i < points.length; i++) {
      while (
        lower.length >= 2 &&
        cross(lower[lower.length - 2], lower[lower.length - 1], points[i]) <= 0
      ) {
        lower.pop()
      }
      lower.push(points[i])
    }

    const upper: Vector2[] = []
    for (let i = points.length - 1; i >= 0; i--) {
      while (
        upper.length >= 2 &&
        cross(upper[upper.length - 2], upper[upper.length - 1], points[i]) <= 0
      ) {
        upper.pop()
      }
      upper.push(points[i])
    }

    lower.pop()
    upper.pop()

    return lower.concat(upper)
  }
}
